import {useEffect, useState, useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import { 
  User, 
  Phone, 
  Mail, 
  Save, 
  Globe, 
  Clock,  
  ToggleLeft,
  ToggleRight,
  Bell,
  MessageSquare,
  Image,
  Settings,
  Zap,
  CreditCard,
  GripVertical,
  ChevronDown,
  ChevronRight,
  DollarSign
} from 'lucide-react';
import {useGetProfile, useToggleSetting, useUpdateNotificationSettings} from '../hooks/useProfile.ts';
import {useIsAdmin} from "../hooks/useIsAdmin.ts";
import { useGroupProfile } from '../hooks/useGroupProfile.ts';
import { useGroupFeatures } from '../hooks/useGroupFeatures.ts';
import { usePaymentRouting } from '../hooks/usePaymentRouting.ts';
import { useContributionDrives } from '../hooks/useContributionDrives.ts';
import { useContributions } from '../hooks/useContributions.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';
// Using Shares API for submissions
import SmsQuotaWidget from '../components/SmsQuotaWidget';
import SmsRechargeModal from '../components/SmsRechargeModal';
import SmsRechargeHistory from '../components/SmsRechargeHistory';
import FeatureSelection from '../components/FeatureSelection';
import WorkflowSettings from '../components/WorkflowSettings';
import SmsTemplateEditor from '../components/SmsTemplateEditor';
import Modal from '../components/Modal.tsx';
import { useSmsTemplate, useSmsTemplatePlaceholders, useSaveSmsTemplate, useDeleteSmsTemplate } from '../hooks/useSmsTemplates.ts';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { Edit, Trash2, CheckCircle, Plus } from 'lucide-react';

// Member Target Stats Component
function MemberTargetStats({ memberId, target, cycle, expectedPerInterval }) {
  const { data: contributionsData, isLoading } = useContributions({
    member_id: memberId,
    date_from: target.from_date,
    date_to: target.to_date,
    per_page: 1000, // Get all contributions in the period
  });

  const { data: drivesData } = useContributionDrives();

  // Calculate total contributed - API returns { data: [...] }, not data.data
  const contributions = contributionsData?.data || [];
  const totalContributed = contributions.reduce((sum, contrib) => sum + (parseFloat(contrib.amount) || 0), 0);

  // Calculate intervals and months late
  const calculateStats = () => {
    if (!target.from_date || !target.to_date || !target.target_amount) {
      return { intervalsPassed: 0, intervalsTotal: 0, monthsLate: 0, canCalculate: false };
    }

    let from = new Date(target.from_date);
    let to = new Date(target.to_date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let intervalsPassed = 0;
    let intervalsTotal = 0;

    // Automatically roll the configured date range forward in cycles of the same length
    // so that once the last day has passed, a new cycle starts without extra user input.
    if (today > to) {
      const cycleLengthMs = Math.max(24 * 60 * 60 * 1000, to.getTime() - from.getTime() + 24 * 60 * 60 * 1000);
      const msSinceStart = today.getTime() - from.getTime();
      const cyclesPassed = Math.floor(msSinceStart / cycleLengthMs);
      if (cyclesPassed > 0) {
        from = new Date(from.getTime() + cyclesPassed * cycleLengthMs);
        to = new Date(to.getTime() + cyclesPassed * cycleLengthMs);
        // If we still ended up beyond the end, move one more cycle
        if (today > to) {
          from = new Date(from.getTime() + cycleLengthMs);
          to = new Date(to.getTime() + cycleLengthMs);
        }
      }
    }

    if (cycle === 'monthly') {
      intervalsTotal = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
      const effectiveEnd = today > to ? to : today;
      intervalsPassed = (effectiveEnd.getFullYear() - from.getFullYear()) * 12 + (effectiveEnd.getMonth() - from.getMonth()) + 1;
      if (intervalsPassed < 0) intervalsPassed = 0;
    } else if (cycle === 'weekly') {
      intervalsTotal = Math.ceil((to - from) / (7 * 24 * 60 * 60 * 1000));
      const effectiveEnd = today > to ? to : today;
      intervalsPassed = Math.ceil((effectiveEnd - from) / (7 * 24 * 60 * 60 * 1000));
      if (intervalsPassed < 0) intervalsPassed = 0;
    } else {
      intervalsTotal = Math.ceil((to - from) / (24 * 60 * 60 * 1000));
      const effectiveEnd = today > to ? to : today;
      intervalsPassed = Math.ceil((effectiveEnd - from) / (24 * 60 * 60 * 1000));
      if (intervalsPassed < 0) intervalsPassed = 0;
    }

    const targetTotal = parseFloat(target.target_amount) || 0;

    // Calculate expected amount up to now (capped at total target)
    let expectedUpToNow = intervalsPassed > 0 ? (targetTotal / intervalsTotal) * intervalsPassed : 0;
    expectedUpToNow = Math.min(expectedUpToNow, targetTotal);
    
    // Calculate months late (only for monthly cycle)
    // Months late = how many months have passed without the expected contribution
    let monthsLate = 0;
    if (cycle === 'monthly' && today > from) {
      // Count how many full months have passed since start date
      const monthsPassed = Math.max(0, (today.getFullYear() - from.getFullYear()) * 12 + (today.getMonth() - from.getMonth()));
      
      // Count unique months with contributions
      const monthsWithContributions = new Set();
      contributions.forEach(contrib => {
        const contribDate = new Date(contrib.contribution_date);
        const monthKey = `${contribDate.getFullYear()}-${contribDate.getMonth()}`;
        monthsWithContributions.add(monthKey);
      });
      
      // Months late = months passed - months with contributions
      monthsLate = Math.max(0, monthsPassed - monthsWithContributions.size);
    }

    // Check if balance can be calculated
    const hasActiveDrives = drivesData?.data?.some(d => 
      ['active', 'pending'].includes(d.status) && d.amount_per_member
    );
    const canCalculate = target.target_amount > 0 || hasActiveDrives;

    // Determine overpayment status
    const isOverpaid = totalContributed > targetTotal && targetTotal > 0;
    const overpaidAmount = isOverpaid ? totalContributed - targetTotal : 0;

    // If member has already contributed at least the expected amount up to now,
    // treat them as not late (even if some months had no separate contributions).
    if (totalContributed >= expectedUpToNow) {
      monthsLate = 0;
    }

    return {
      intervalsPassed,
      intervalsTotal,
      monthsLate,
      expectedUpToNow,
      canCalculate,
      isOverpaid,
      overpaidAmount,
      targetTotal,
    };
  };

  const stats = calculateStats();
  const cycleLabel = cycle === 'daily' ? 'day' : cycle === 'weekly' ? 'week' : 'month';

  return (
    <div className="mt-2 space-y-2">
      <div className="p-2 bg-blue-50 rounded text-xs">
        <div className="flex items-center justify-between mb-1">
          <span className="text-blue-700">
            Expected: <strong>{expectedPerInterval} KES</strong> per {cycleLabel}
          </span>
          <span className={stats.canCalculate ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
            {stats.canCalculate ? '✓ Balance OK' : '⚠ Balance unavailable'}
          </span>
        </div>
        {!isLoading && (
          <div className="mt-1 pt-1 border-t border-blue-200">
            <div className={stats.isOverpaid ? 'text-green-700 font-medium' : 'text-blue-600'}>
              {stats.isOverpaid ? (
                <>
                  Overpaid:{' '}
                  <strong>{totalContributed.toFixed(2)} KES</strong>{' '}
                  (target <strong>{stats.targetTotal.toFixed(2)} KES</strong>, over by{' '}
                  <strong>{stats.overpaidAmount.toFixed(2)} KES</strong>)
                </>
              ) : (
                <>
                  Contributed:{' '}
                  <strong>{totalContributed.toFixed(2)} KES</strong> /{' '}
                  <strong>{stats.expectedUpToNow.toFixed(2)} KES</strong> expected
                </>
              )}
            </div>
            {!stats.isOverpaid && cycle === 'monthly' && stats.monthsLate > 0 && (
              <div className="text-red-600 mt-1 font-medium">
                ⚠ {stats.monthsLate} {stats.monthsLate === 1 ? 'month' : 'months'} late
              </div>
            )}
          </div>
        )}
        {isLoading && (
          <div className="mt-1 text-blue-500 text-xs">Loading contribution data...</div>
        )}
      </div>
    </div>
  );
}

// SMS Templates Content Component
function SmsTemplatesContent({ localPaymentRoutingSettings, profile }) {
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const { data: paymentRoutingSettings } = usePaymentRouting();
  
  // Use local settings if available, otherwise fall back to saved settings
  const effectiveSettings = localPaymentRoutingSettings || paymentRoutingSettings;
  
  const isHouseManagement = Number(profile?.data?.current_group?.is_house_management) === 1;
  const houseLabel = profile?.data?.current_group?.house_label || 'House';

  const dueRemindersEnabled = paymentRoutingSettings?.due_reminders_enabled ?? false;
  const lateRemindersEnabled = paymentRoutingSettings?.late_reminders_enabled ?? false;
  const hasReminderSettings = paymentRoutingSettings?.due_reminders_enabled !== undefined || paymentRoutingSettings?.late_reminders_enabled !== undefined;
  const notifyMemberEnabled = paymentRoutingSettings?.notify_member_on_payment ?? true;
  const notifyAdminEnabled = paymentRoutingSettings?.notify_admin_on_payment ?? true;
  const hasNotificationSettings = paymentRoutingSettings?.notify_member_on_payment !== undefined;
  const welcomeNotificationEnabled = profile?.data?.current_group?.notify_member_of_new_account ?? true;
  
  // Template configurations - easy to add more in the future
  const templates = [
    {
      type: 'contribution_confirmation',
      name: 'Contribution Confirmation',
      description: 'This template is used when a member makes a contribution. The message will automatically adjust based on payment status (normal, fully paid, or overpaid).',
      color: 'blue',
      requiresReminders: false,
      requiresNotificationSetting: 'notify_member_on_payment',
    },
    {
      type: 'admin_payment_notification',
      name: 'Admin Payment Notification',
      description: 'This template is sent to admins, treasurers, and secretaries when a member makes a payment. Used for automatic payments made by members.',
      color: 'purple',
      requiresReminders: false,
      requiresNotificationSetting: 'notify_admin_on_payment',
    },
    {
      type: 'admin_manual_contribution_notification',
      name: 'Admin Manual Contribution Notification',
      description: 'This template is sent to admins, treasurers, and secretaries when a contribution is added manually by an admin. Includes who added the contribution.',
      color: 'indigo',
      requiresReminders: false,
      requiresNotificationSetting: 'notify_admin_on_payment',
    },
    {
      type: 'due_reminder',
      name: 'Due Payment Reminder',
      description: 'This template is used to remind members about upcoming payment due dates. Sent before the due date based on your reminder settings.',
      color: 'green',
      requiresDueReminders: true,
      requiresNotificationSetting: null,
    },
    {
      type: 'late_reminder',
      name: 'Late Payment Reminder',
      description: 'This template is used to remind members about overdue payments. Sent after the due date based on your reminder frequency settings.',
      color: 'orange',
      requiresLateReminders: true,
      requiresNotificationSetting: null,
    },
    {
      type: 'welcome_message',
      name: 'New Member Welcome Message',
      description: 'Sent to new members when they are onboarded to the group.',
      color: 'teal',
      requiresReminders: false,
      requiresNotificationSetting: null,
      requiresWelcomeNotification: true,
    },
    // Add more templates here in the future
  ];

  const toggleTemplate = (templateType) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateType)) {
        newSet.delete(templateType);
      } else {
        newSet.add(templateType);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-4">
      {templates.map((templateConfig) => {
        // Check if template should be disabled based on reminder settings
        let isDisabled = false;
        let disableReason = '';
        
        if (templateConfig.requiresDueReminders) {
          isDisabled = !hasReminderSettings || !dueRemindersEnabled;
          if (!hasReminderSettings) {
            disableReason = '⚠️ Payment reminder settings not configured. Enable reminders in Payment Routing settings first.';
          } else if (!dueRemindersEnabled) {
            disableReason = '⚠️ Due Payment reminders are disabled. Enable them in Payment Routing settings to use this template.';
          }
        }

        if (templateConfig.requiresLateReminders) {
          isDisabled = !hasReminderSettings || !lateRemindersEnabled;
          if (!hasReminderSettings) {
            disableReason = '⚠️ Payment reminder settings not configured. Enable reminders in Payment Routing settings first.';
          } else if (!lateRemindersEnabled) {
            disableReason = '⚠️ Late Payment reminders are disabled. Enable them in Payment Routing settings to use this template.';
          }
        }
        
        // Check if template should be disabled based on notification settings
        if (templateConfig.requiresNotificationSetting) {
          const settingEnabled = templateConfig.requiresNotificationSetting === 'notify_member_on_payment' 
            ? notifyMemberEnabled 
            : notifyAdminEnabled;
          
          if (!hasNotificationSettings) {
            isDisabled = true;
            disableReason = '⚠️ Notification settings not configured. Configure notifications in Payment Routing settings first.';
          } else if (!settingEnabled) {
            isDisabled = true;
            const settingName = templateConfig.requiresNotificationSetting === 'notify_member_on_payment'
              ? 'Notify member on payment'
              : 'Notify admin on payment';
            disableReason = `⚠️ ${settingName} is disabled. Enable it in Payment Routing settings to use this template.`;
          }
        }

        // Check welcome notification setting
        if (templateConfig.requiresWelcomeNotification && !welcomeNotificationEnabled) {
          isDisabled = true;
          disableReason = '⚠️ Welcome SMS notification is disabled. Enable "Notify member on new account" in Notifications settings to use this template.';
        }
        
        return (
          <TemplateSection
            key={templateConfig.type}
            templateConfig={templateConfig}
            isExpanded={expandedTemplates.has(templateConfig.type)}
            onToggle={() => !isDisabled && toggleTemplate(templateConfig.type)}
            isDisabled={isDisabled}
            disableReason={disableReason}
            hasReminderSettings={hasReminderSettings}
            localPaymentRoutingSettings={effectiveSettings}
            isHouseManagement={isHouseManagement}
            houseLabel={houseLabel}
          />
        );
      })}

      <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
        <h4 className="mb-2 text-sm font-semibold text-gray-900">Template Information</h4>
        <div className="space-y-3 text-xs text-gray-600">
          {isHouseManagement ? (
            <>
              <div>
                <p className="mb-1 font-medium text-gray-900">{houseLabel} Placeholders:</p>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li><code className="bg-gray-100 px-1 rounded">{'{subgroup_name}'}</code> — The {houseLabel.toLowerCase()} name/number the member belongs to</li>
                  <li><code className="bg-gray-100 px-1 rounded">{'{subgroup_balance}'}</code> — Collective {houseLabel.toLowerCase()} balance (total contributions vs target). Available once {houseLabel.toLowerCase()} targets are set.</li>
                </ul>
              </div>
              <div>
                <p className="mb-1 font-medium text-gray-900">Reminder Templates:</p>
                <p className="text-gray-600">
                  Reminder templates reference the member's {houseLabel.toLowerCase()} ({'{subgroup_name}'}) so reminders are contextual to each {houseLabel.toLowerCase()}.
                  Configure reminder timing in the <strong>Payment Routing</strong> tab.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="mb-1 font-medium text-gray-900">Contribution Confirmation:</p>
                <p className="text-gray-600">
                  The system will automatically append status messages if not included in your template:
                </p>
                <ul className="mt-1 ml-4 space-y-1 list-disc">
                  <li><strong>Fully Paid:</strong> "You are fully paid."</li>
                  <li><strong>Overpaid:</strong> "You have overpaid by KES X.XX."</li>
                </ul>
              </div>
              <div>
                <p className="mb-1 font-medium text-gray-900">Reminder Templates:</p>
                <p className="text-gray-600">
                  Reminder templates are used by the automated payment reminder system. 
                  Configure reminder timing in the <strong>Payment Routing</strong> tab.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual Template Section Component
function TemplateSection({ templateConfig, isExpanded, onToggle, isDisabled = false, disableReason = '', hasReminderSettings = false, localPaymentRoutingSettings = null, isHouseManagement = false, houseLabel = 'House' }) {
  const { data: templateData, isLoading: isLoadingTemplate } = useSmsTemplate(templateConfig.type);
  const { data: placeholdersData } = useSmsTemplatePlaceholders(templateConfig.type);
  const saveTemplate = useSaveSmsTemplate();
  const deleteTemplate = useDeleteSmsTemplate();
  const { data: drivesData } = useContributionDrives();
  const { data: paymentRoutingSettings } = usePaymentRouting();

  const template = templateData?.data;
  const allPlaceholders = placeholdersData?.data || {};
  
  // Check if balance can be calculated - use local settings if available, otherwise saved settings
  // This ensures we check unsaved changes first, then fall back to saved state
  const canCalculateBalance = (() => {
    // Priority: If localPaymentRoutingSettings is explicitly provided (not null), use it
    // This means user is on Payment Routing tab and we should check their current edits
    // If null, fall back to saved settings (user might be on SMS Templates tab)
    const settingsToCheck = localPaymentRoutingSettings !== null && localPaymentRoutingSettings !== undefined 
      ? localPaymentRoutingSettings 
      : paymentRoutingSettings;
    
    // If no settings at all, balance can't be calculated
    if (!settingsToCheck) {
      return false;
    }
    
    // If payment routing not enabled (no contribution amount), balance can't be calculated
    if (!settingsToCheck.contribution_amount || settingsToCheck.contribution_amount <= 0) {
      return false;
    }
    
    // Check if member targets exist
    const memberTargets = settingsToCheck.member_targets || {};
    const hasMemberTargets = Object.keys(memberTargets).length > 0 && 
      Object.values(memberTargets).some(target => {
        if (typeof target === 'number') {
          return target > 0;
        }
        if (typeof target === 'object' && target !== null) {
          return target.target_amount > 0;
        }
        return false;
      });

    // For template display: Balance can be calculated ONLY if member targets are explicitly set
    // This ensures the template reflects the current setup state
    // User must configure member targets before balance placeholders appear in templates
    return hasMemberTargets;
  })();
  
  // Balance-related placeholders that should be hidden when balance can't be calculated
  const balancePlaceholders = ['balance', 'payment_status', 'overpaid_amount', 'contribution_balance'];
  // Subgroup balance placeholder hidden when subgroup targets aren't set
  const subgroupBalancePlaceholders = ['subgroup_balance'];
  
  // For house management: balance is available when subgroup_targets are configured
  const canCalculateSubgroupBalance = (() => {
    const settingsToCheck = localPaymentRoutingSettings !== null && localPaymentRoutingSettings !== undefined
      ? localPaymentRoutingSettings
      : paymentRoutingSettings;
    if (!settingsToCheck) return false;
    const subgroupTargets = settingsToCheck.subgroup_targets || {};
    return Object.keys(subgroupTargets).length > 0 && Object.values(subgroupTargets).some(t => {
      if (typeof t === 'number') return t > 0;
      if (typeof t === 'object' && t !== null) return (t.target_amount || 0) > 0;
      return false;
    });
  })();

  // Filter out balance placeholders if balance can't be calculated
  const availablePlaceholders = Object.keys(allPlaceholders).filter(ph => {
    if (isHouseManagement) {
      // For house groups, hide subgroup_balance if subgroup targets aren't set
      if (subgroupBalancePlaceholders.includes(ph)) return canCalculateSubgroupBalance;
      // Hide member-level balance placeholders entirely
      if (balancePlaceholders.includes(ph)) return false;
      return true;
    }
    if (balancePlaceholders.includes(ph)) {
      return canCalculateBalance;
    }
    return true;
  });
  
  // Filter placeholder descriptions to match available placeholders
  const placeholderDescriptions = Object.fromEntries(
    availablePlaceholders.map(ph => [ph, allPlaceholders[ph]])
  );
  
  // Use the template as-is - backend already returns the correct template based on balance calculation
  // No need for regex removal since backend handles it properly
  const displayTemplate = template?.template || '';

  const handleSave = (templateText) => {
    saveTemplate.mutate(
      {
        template_type: templateConfig.type,
        template: templateText,
        name: templateConfig.name,
      },
      {
        onSuccess: () => {
          toast.success('Template saved successfully!');
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error) || 'Failed to save template');
        },
      }
    );
  };

  const handleDelete = () => {
    deleteTemplate.mutate(templateConfig.type, {
      onSuccess: () => {
        toast.success('Template deleted. System default will be used.');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to delete template');
      },
    });
  };

  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/40 dark:border-blue-500/60 dark:text-blue-100',
    green: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900/40 dark:border-green-500/60 dark:text-green-100',
    // Admin Payment Notification (purple) needs especially good contrast in dark mode
    purple: 'bg-purple-50 border-purple-200 text-purple-900 dark:bg-purple-900/50 dark:border-purple-400 dark:text-purple-100',
    orange: 'bg-orange-50 border-orange-200 text-orange-900 dark:bg-orange-900/40 dark:border-orange-500/60 dark:text-orange-100',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-900/40 dark:border-indigo-500/60 dark:text-indigo-100',
  };

  const bgColor = colorClasses[templateConfig.color] || colorClasses.blue;

  return (
    <div className={`rounded-lg border ${
      isDisabled
        ? 'bg-gray-100 border-gray-300 opacity-60 dark:bg-gray-800 dark:border-gray-600'
        : bgColor
    }`}>
      <button
        onClick={onToggle}
        disabled={isDisabled}
        className={`flex justify-between items-center p-4 w-full text-left transition-opacity ${
          isDisabled ? 'cursor-not-allowed' : 'hover:opacity-80'
        }`}
      >
        <div className="flex items-center space-x-3">
          {isDisabled ? (
            <ChevronRight size={20} className="text-gray-400" />
          ) : isExpanded ? (
            <ChevronDown size={20} className="text-gray-600" />
          ) : (
            <ChevronRight size={20} className="text-gray-600" />
          )}
          <div>
            <h3 className={`text-lg font-semibold ${isDisabled ? 'text-gray-500' : ''}`}>
              {templateConfig.name}
            </h3>
            <p className={`text-sm ${isDisabled ? 'text-gray-400' : 'opacity-80'}`}>
              {templateConfig.description}
            </p>
            {isDisabled && disableReason && (
              <p className="mt-1 text-xs text-orange-600 font-medium">
                {disableReason}
              </p>
            )}
          </div>
        </div>
        {!isDisabled && template?.is_custom && (
          <span className="px-2 py-1 text-xs font-medium bg-white rounded border border-current opacity-70">
            Custom
          </span>
        )}
      </button>

      {isExpanded && !isDisabled && (
        <div className="px-4 pb-6 border-t border-current border-opacity-20">
          {isLoadingTemplate ? (
            <div className="pt-4 text-center">
              <div className="mx-auto w-6 h-6 rounded-full border-b-2 border-current opacity-60 animate-spin"></div>
              <p className="mt-2 text-sm opacity-70">Loading template...</p>
            </div>
          ) : (
            <div className="pt-4 space-y-4">
              {template?.is_custom === false && (
                <div className="p-3 bg-white rounded border border-current border-opacity-30">
                  <p className="text-xs">
                    <strong>Currently using system default template.</strong> Create a custom template below to override it.
                  </p>
                </div>
              )}

              <SmsTemplateEditor
                templateType={templateConfig.type}
                template={displayTemplate}
                availablePlaceholders={availablePlaceholders}
                placeholderDescriptions={placeholderDescriptions}
                onSave={handleSave}
                onDelete={template?.is_custom ? handleDelete : undefined}
                isLoading={saveTemplate.isPending || deleteTemplate.isPending}
                label="Template Message"
                rows={4}
                maxLength={500}
                canCalculateBalanceOverride={canCalculateBalance}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Loan Settings Content Component (embedded from LoanSettingsPage)
function LoanSettingsContent() {
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [mode, setMode] = useState('');
  const [amount, setAmount] = useState('');
  const [multiple, setMultiple] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSetting, setCurrentSetting] = useState(null);
  const [loanTypes, setLoanTypes] = useState([]);
  const [loanTypeSettings, setLoanTypeSettings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const { can } = usePermissions();
  
  // State for conditional display
  const [showFixedAmount, setShowFixedAmount] = useState(false);
  const [showContributionMultiple, setShowContributionMultiple] = useState(false);

  // Loan arrears reminder settings state
  const [arrearsSettings, setArrearsSettings] = useState({
    enable_loan_arrears_reminders: false,
    loan_arrears_reminder_frequency: 'weekly',
    loan_arrears_grace_days: 0,
    loan_arrears_reminder_message: ''
  });
  const [arrearsLoading, setArrearsLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState({});

  const MODES = [
    { value: 'fixed_amount', label: 'Fixed Amount' },
    { value: 'contribution_multiple', label: 'Contribution Multiple' },
  ];

  useEffect(() => {
    // Load loan types
    api.get('/loan-type').then(res => {
      setLoanTypes(res.data.data || []);
    });

    // Load loan type settings
    api.get('/loan-eligibility-settings/loan-types').then(res => {
      setLoanTypeSettings(res.data.data || []);
    });

    // Load loan arrears reminder settings
    api.get('/groups/loan-arrears-settings').then(res => {
      if (res.data.success) {
        setArrearsSettings(res.data.data);
        if (res.data.placeholders) {
          setPlaceholders(res.data.placeholders);
        }
      }
    }).catch(err => {
      console.error('Failed to load arrears settings:', err);
    });
  }, []);

  // useEffect to handle conditional display based on mode
  useEffect(() => {
    if (mode === 'fixed_amount') {
      setShowFixedAmount(true);
      setShowContributionMultiple(false);
    } else if (mode === 'contribution_multiple') {
      setShowFixedAmount(false);
      setShowContributionMultiple(true);
    } else {
      setShowFixedAmount(false);
      setShowContributionMultiple(false);
    }
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { 
        mode,
        loan_type_id: selectedLoanType?.id 
      };
      if (mode === 'fixed_amount') payload.amount = Number(amount);
      if (mode === 'contribution_multiple') payload.multiple = Number(multiple);
      
      await api.post('/loan-eligibility-settings', payload);
      toast.success('Loan eligibility settings updated');
      
      // Refresh loan type settings
      const res = await api.get('/loan-eligibility-settings/loan-types');
      setLoanTypeSettings(res.data.data || []);
      
      // Reset form
      setSelectedLoanType(null);
      setMode('');
      setAmount('');
      setMultiple('');
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update settings');
    }
    setLoading(false);
  };

  const handleEdit = (setting) => {
    setSelectedLoanType(setting.loan_type);
    setMode(setting.mode);
    setAmount(setting.amount?.toString() || '');
    setMultiple(setting.multiple?.toString() || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSelectedLoanType(null);
    setMode('');
    setAmount('');
    setMultiple('');
    setIsEditing(false);
  };

  const handleArrearsSettingsSubmit = async (e) => {
    e.preventDefault();
    setArrearsLoading(true);
    try {
      const response = await api.post('/groups/loan-arrears-settings', arrearsSettings);
      if (response.data.success) {
        toast.success('Loan arrears reminder settings updated successfully');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update arrears settings');
    }
    setArrearsLoading(false);
  };

  // Helper to show the current value for the selected mode
  const renderCurrentModeValue = () => {
    if (!currentSetting || !mode) return null;
    if (mode === 'fixed_amount' && currentSetting.mode === 'fixed_amount') {
      return (
        <div className="mb-2 text-blue-700 text-sm">Current Fixed Amount: <span className="font-semibold">KES {currentSetting.amount}</span></div>
      );
    }
    if (mode === 'contribution_multiple' && currentSetting.mode === 'contribution_multiple') {
      return (
        <div className="mb-2 text-blue-700 text-sm">Current Contribution Multiple: <span className="font-semibold">{currentSetting.multiple}x</span></div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Existing Loan Settings Content - moved from LoanSettingsPage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Loan Type Settings List */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Loan Type Eligibility Settings</h3>
          
          {loanTypeSettings.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No loan type eligibility settings configured yet.
            </div>
          ) : (
            <div className="space-y-3">
              {loanTypeSettings.map((setting) => (
                <div key={setting.id} className="border rounded-lg p-4 hover:bg-white bg-white">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{setting.loan_type?.name}</h4>
                      <div className="text-sm text-gray-600 mt-1">
                        {setting.mode === 'fixed_amount' ? (
                          <span>Fixed Amount: KES {setting.amount?.toLocaleString()}</span>
                        ) : (
                          <span>Contribution Multiple: {setting.multiple}x</span>
                        )}
                      </div>
                    </div>
                    {can(PERMISSIONS.UPDATE_LOAN_ELIGIBILITY_SETTING) && (
                      <button
                        onClick={() => handleEdit(setting)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Form */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {isEditing ? 'Edit Eligibility Setting' : 'Add New Eligibility Setting'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Loan Type Selection */}
            <div>
              <label className="block mb-2 font-medium text-gray-700">Loan Type</label>
              <select 
                value={selectedLoanType?.id || ''} 
                onChange={e => {
                  const loanType = loanTypes.find(lt => lt.id === parseInt(e.target.value));
                  setSelectedLoanType(loanType);
                }}
                className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={isEditing}
              >
                <option value="">Select a loan type</option>
                {loanTypes.map(loanType => (
                  <option key={loanType.id} value={loanType.id}>
                    {loanType.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Eligibility Mode */}
            <div>
              <label className="block mb-2 font-medium text-gray-700">Eligibility Mode</label>
              <select 
                value={mode} 
                onChange={e => setMode(e.target.value)} 
                className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select mode</option>
                {MODES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Fixed Amount Input */}
            {showFixedAmount && (
              <div>
                {renderCurrentModeValue()}
                <label className="block mb-2 font-medium text-gray-700">Fixed Amount (KES)</label>
                <input 
                  type="number" 
                  min="0" 
                  value={amount} 
                  onChange={e => setAmount(e.target.value)} 
                  className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                  required 
                />
              </div>
            )}

            {/* Contribution Multiple Input */}
            {showContributionMultiple && (
              <div>
                {renderCurrentModeValue()}
                <label className="block mb-2 font-medium text-gray-700">Contribution Multiple</label>
                <input 
                  type="number" 
                  step="0.1"
                  min="0"
                  value={multiple} 
                  onChange={e => setMultiple(e.target.value)} 
                  className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                  required 
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button 
                type="submit" 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" 
                disabled={loading || !mode || !selectedLoanType}
              >
                {loading ? 'Saving...' : (isEditing ? 'Update Setting' : 'Add Setting')}
              </button>
              
              {isEditing && (
                <button 
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Loan Arrears Reminder Settings - moved from LoanSettingsPage */}
      <div className="bg-gray-50 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold mb-4">Loan Arrears Reminder Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure automatic SMS reminders for members with overdue loan payments.
        </p>
        
        <form onSubmit={handleArrearsSettingsSubmit} className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg">
            <div className="flex-1">
              <label className="block font-medium text-gray-900">
                Enable Loan Arrears Reminders
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Send automatic SMS reminders to members with overdue loans
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={arrearsSettings.enable_loan_arrears_reminders}
                onChange={(e) => setArrearsSettings({
                  ...arrearsSettings,
                  enable_loan_arrears_reminders: e.target.checked
                })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Reminder Frequency */}
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Reminder Frequency
            </label>
            <select
              value={arrearsSettings.loan_arrears_reminder_frequency}
              onChange={(e) => setArrearsSettings({
                ...arrearsSettings,
                loan_arrears_reminder_frequency: e.target.value
              })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!arrearsSettings.enable_loan_arrears_reminders}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
              <option value="monthly">Monthly</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How often should reminders be sent to members with overdue loans
            </p>
          </div>

          {/* Grace Period */}
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Grace Period (Days)
            </label>
            <input
              type="number"
              min="0"
              max="365"
              value={arrearsSettings.loan_arrears_grace_days}
              onChange={(e) => setArrearsSettings({
                ...arrearsSettings,
                loan_arrears_grace_days: parseInt(e.target.value) || 0
              })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!arrearsSettings.enable_loan_arrears_reminders}
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of days after the due date before sending reminders (0 = send immediately after due date)
            </p>
          </div>

          {/* Message Template */}
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Message Template
            </label>
            <textarea
              rows="4"
              value={arrearsSettings.loan_arrears_reminder_message}
              onChange={(e) => setArrearsSettings({
                ...arrearsSettings,
                loan_arrears_reminder_message: e.target.value
              })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              disabled={!arrearsSettings.enable_loan_arrears_reminders}
              placeholder="Enter your custom message template..."
              maxLength="500"
            />
            <div className="flex justify-between items-start mt-1">
              <p className="text-xs text-gray-500">
                Customize the SMS message sent to members. Use placeholders below.
              </p>
              <span className="text-xs text-gray-400">
                {arrearsSettings.loan_arrears_reminder_message?.length || 0}/500
              </span>
            </div>
            
            {/* Available Placeholders */}
            {Object.keys(placeholders).length > 0 && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Available Placeholders:</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(placeholders).map(([key, description]) => (
                    <div key={key} className="flex items-start">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-300 text-blue-600 font-mono">
                        {key}
                      </code>
                      <span className="text-xs text-gray-600 ml-2">- {description}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const defaultMsg = 'Dear {member_name}, your loan of {loan_amount} is overdue by {days_overdue} days. Outstanding balance: {outstanding_balance}. Please make payment to avoid penalties. Thank you.';
                    setArrearsSettings({
                      ...arrearsSettings,
                      loan_arrears_reminder_message: defaultMsg
                    });
                  }}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Reset to default template
                </button>
              </div>
            )}
          </div>

          {/* Info Box */}
          {arrearsSettings.enable_loan_arrears_reminders && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">
                    Reminder Schedule
                  </h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Members with overdue loans will receive SMS reminders{' '}
                      <strong>{arrearsSettings.loan_arrears_reminder_frequency}</strong>
                      {arrearsSettings.loan_arrears_grace_days > 0 && (
                        <span>, starting {arrearsSettings.loan_arrears_grace_days} day(s) after the due date</span>
                      )}
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          {can(PERMISSIONS.UPDATE_LOAN_ELIGIBILITY_SETTING) && (
            <div className="flex justify-end pt-4 border-t">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={arrearsLoading}
              >
                {arrearsLoading ? 'Saving...' : 'Save Arrears Settings'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// Table Banking Settings Content Component (embedded from TableBankingSettingsPage)
function TableBankingSettingsContent() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);

  const [formData, setFormData] = useState({
    interest_rate: "",
    repayment_period: "",
    penalty_amount: "",
    lending_limit: "",
    initial_amount: ""
  });

  const fetchSettings = async () => {
    try {
      const res = await api.get("/tablebanking-setting");
      setSettings(res.data?.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch table banking settings");
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      interest_rate: parseFloat(formData.interest_rate),
      repayment_period: parseInt(formData.repayment_period),
      penalty_amount: parseFloat(formData.penalty_amount),
      lending_limit: parseFloat(formData.lending_limit),
      initial_amount: parseFloat(formData.initial_amount)
    };

    try {
      if (editingSetting) {
        await api.put(`/tablebanking-setting/${editingSetting.id}`, payload);
        toast.success("Setting updated successfully");
      } else {
        await api.post("/tablebanking-setting", payload);
        toast.success("Setting created successfully");
      }

      setFormData({
        interest_rate: "",
        repayment_period: "",
        penalty_amount: "",
        lending_limit: "",
        initial_amount: ""
      });
      setEditingSetting(null);
      fetchSettings();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save setting");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      interest_rate: setting.interest_rate.toString(),
      repayment_period: setting.repayment_period.toString(),
      penalty_amount: setting.penalty_amount.toString(),
      lending_limit: setting.lending_limit.toString(),
      initial_amount: setting.initial_amount.toString()
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this setting?")) return;
    try {
      await api.delete(`/tablebanking-setting/${id}`);
      toast.success("Setting deleted");
      fetchSettings();
    } catch {
      toast.error("Failed to delete setting");
    }
  };

  const handleCancelEdit = () => {
    setEditingSetting(null);
    setFormData({
      interest_rate: "",
      repayment_period: "",
      penalty_amount: "",
      lending_limit: "",
      initial_amount: ""
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Settings List */}
      <div className="bg-gray-50 p-5 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Existing Settings
        </h3>

        {settings.length === 0 ? (
          <p className="text-gray-500 text-sm">No settings found.</p>
        ) : (
          <div className="space-y-3">
            {settings.map((setting) => (
              <div
                key={setting.id}
                className="p-4 border rounded bg-white flex justify-between items-start hover:shadow-sm"
              >
                <div>
                  <p className="font-medium text-gray-800">
                    Interest Rate: {setting.interest_rate}%
                  </p>
                  <p className="text-sm text-gray-600">
                    Repayment: {setting.repayment_period} days
                  </p>
                  <p className="text-sm text-gray-600">
                    Penalty: KES {setting.penalty_amount}
                  </p>
                  <p className="text-sm text-gray-600">
                    Lending Limit: KES {setting.lending_limit.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    TableBankingPool balance: KES {setting.remaining_amount}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(setting)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(setting.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {(editingSetting || settings.length === 0) && (
        <div className="bg-gray-50 p-5 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {editingSetting ? "Edit Setting" : "Add New Setting"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Interest Rate (%)
              </label>
              <input
                type="number"
                name="interest_rate"
                step="0.1"
                value={formData.interest_rate}
                onChange={handleChange}
                className="border w-full rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Repayment Period (days)
              </label>
              <input
                type="number"
                name="repayment_period"
                value={formData.repayment_period}
                onChange={handleChange}
                className="border w-full rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                TableBankingPool Amount 
              </label>
              <input
                type="number"
                name="initial_amount"
                value={formData.initial_amount}
                onChange={handleChange}
                className="border w-full rounded p-2"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter an amount that will be set aside for table banking loans.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Penalty Amount (KES)
              </label>
              <input
                type="number"
                name="penalty_amount"
                value={formData.penalty_amount}
                onChange={handleChange}
                className="border w-full rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Lending Limit (KES)
              </label>
              <input
                type="number"
                name="lending_limit"
                value={formData.lending_limit}
                onChange={handleChange}
                className="border w-full rounded p-2"
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : editingSetting
                  ? "Update Setting"
                  : "Add Setting"}
              </button>

              {editingSetting && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="border px-4 py-2 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function TwachangaSettings() {
  // User profile settings
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const updateToggleSetting  = useToggleSetting();
  const updateNotificationSettings = useUpdateNotificationSettings();
  const { updateGroupProfile, isUpdating: isUpdatingGroupProfile } = useGroupProfile();
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const { hasFeature } = useGroupFeatures();

  // Notification settings
  const [notifications, setNotifications] = useState({
    driveReminders: true,
    paymentConfirmations: true,
    weeklyReports: true,
    groupMessages: false,
    enable_notifications: false,
    notify_member_of_new_account: false,
    can_view_other_members: false,
    contribution_reminder: false,
    contribution_summary: false,
    contribution_summary_frequency: 'monthly',
    contribution_reminder_frequency: 'monthly',
    contribution_reminder_schedule_value: '',
    contribution_reminder_specific_date: ''
  });

  useEffect(() => {
    if (!isProfileLoading && profile?.data?.current_group) {
      setNotifications(prev => ({
        ...prev,
        enable_notifications: !!profile.data.current_group.enable_notifications,
        notify_member_of_new_account: !!profile.data.current_group.notify_member_of_new_account,
        can_view_other_members: !!profile.data.current_group.can_view_other_members,
        contribution_reminder: !!profile.data.current_group.contribution_reminder,
        contribution_summary: !!profile.data.current_group.contribution_summary,
        contribution_summary_frequency: profile.data.current_group.contribution_summary_frequency || 'monthly',
        contribution_reminder_frequency: profile.data.current_group.contribution_reminder_frequency || 'monthly',
        contribution_reminder_schedule_value: profile.data.current_group.contribution_reminder_schedule_value || '',
        contribution_reminder_specific_date: profile.data.current_group.contribution_reminder_specific_date || '',
      }));
    }
  }, [isProfileLoading, profile]);

  // Security settings
  const [security, setSecurity] = useState({
    twoFactorAuth: false,
    shareContacts: true
  });

  // Appearance settings
  const [appearance, setAppearance] = useState({
    language: "English",
    theme: "Light",
    timezone: "Africa/Nairobi"
  });

  // Get tab from URL query parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const rechargeFromUrl = searchParams.get('recharge');
  
  // Navigation state - initialize from URL or default to 'profile'
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  
  // Loans sub-tab state
  const [loansSubTab, setLoansSubTab] = useState('loan-settings'); // 'loan-settings' or 'table-banking'

  // Update active tab when URL query parameter changes
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open recharge modal if recharge parameter is in URL
  useEffect(() => {
    if (rechargeFromUrl === 'true' && activeTab === 'sms-quota' && isAdmin) {
      // Small delay to ensure tab is rendered first
      const timer = setTimeout(() => {
        setShowRechargeModal(true);
        // Remove the recharge parameter from URL after opening modal
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('recharge');
        setSearchParams(newParams, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [rechargeFromUrl, activeTab, isAdmin, searchParams, setSearchParams]);

  // Update URL when active tab changes (but only if it's different from URL)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab !== tabFromUrl) {
      setSearchParams({ tab: newTab });
    }
  };

  // Redirect non-admin or village admin users away from admin-only settings tabs
  useEffect(() => {
    const userRole = profile?.data?.current_role || profile?.data?.role?.name || profile?.data?.user?.role?.name;
    const isVillageAdmin = userRole === 'village_admin' && !isAdmin;
    if (isVillageAdmin || (!isAdmin && (activeTab === 'sms-quota' || activeTab === 'mpesa' || activeTab === 'notifications' || activeTab === 'contribution' || activeTab === 'general' || activeTab === 'loans' || activeTab === 'payment-routing' || activeTab === 'sms-templates' || activeTab === 'features'))) {
      setActiveTab('profile');
      setSearchParams({});
    }
    // Redirect admin users away from advanced feature tabs if they don't have access
    if (isAdmin && !isVillageAdmin) {
      if ((activeTab === 'mpesa' || activeTab === 'payment-routing') && !hasFeature('mpesa_paybill_integration')) {
        setActiveTab('profile');
        setSearchParams({});
      }
      if (activeTab === 'loans' && !hasFeature('advanced_loan_management')) {
        setActiveTab('profile');
        setSearchParams({});
      }
    }
  }, [isAdmin, activeTab, setSearchParams, hasFeature, profile]);

  //bank_paybill_number, bank_account_no
  // MPESA form state
  const [mpesaForm, setMpesaForm] = useState({
    shortcode: '',
    paybill_number: '',
    consumer_key: '',
    consumer_secret: '',
    security_credential: '',
    stk_push_url: '',
    stk_auth_url: '',
    environment: 'production',
    is_active: true,
    bank_account_reference: ''
  });
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [mpesaSuccess, setMpesaSuccess] = useState(false);
  const [mpesaError, setMpesaError] = useState('');
  // Field-level validation errors returned by the API (422 with `errors` object).
  const [mpesaFieldErrors, setMpesaFieldErrors] = useState({});
  const [showBankAccountFields, setShowBankAccountFields] = useState(false);

  // Bank transfer form state
  const [bankForm, setBankForm] = useState({
    paybill_number: '',
    account_number: '',
    is_active: true,
  });
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSuccess, setBankSuccess] = useState(false);
  const [bankError, setBankError] = useState('');
  
  // Proxy form state
  const [proxyForm, setProxyForm] = useState({
    shortcode: '',
  });
  const [proxyLoading, setProxyLoading] = useState(false);
  const [proxyError, setProxyError] = useState('');
  const [proxySuccess, setProxySuccess] = useState(false);
  
  const [integrationType, setIntegrationType] = useState('mpesa');
  const [selectedCredentialId, setSelectedCredentialId] = useState(null);
  const [isCreatingNewAccount, setIsCreatingNewAccount] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [isDefaultAccount, setIsDefaultAccount] = useState(false);

  // SMS Quota state
  const [showRechargeModal, setShowRechargeModal] = useState(false);

  // Payment Routing state
  const {
    data: paymentRoutingSettings,
    isLoading: isLoadingPaymentRouting,
    updatePaymentRoutingSettings,
    resetPaymentRoutingSettings
  } = usePaymentRouting();
  
  const { data: drivesData } = useContributionDrives();
  
  const [localPaymentRoutingSettings, setLocalPaymentRoutingSettings] = useState(null);
  const [hasInteractedPaymentRouting, setHasInteractedPaymentRouting] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [memberTargetsFromDate, setMemberTargetsFromDate] = useState('');
  const [memberTargetsToDate, setMemberTargetsToDate] = useState('');
  const [availableMembersForTargets, setAvailableMembersForTargets] = useState([]);
  const [availableSubgroupsForTargets, setAvailableSubgroupsForTargets] = useState([]);
  const [subgroupTargetsFromDate, setSubgroupTargetsFromDate] = useState('');
  const [subgroupTargetsToDate, setSubgroupTargetsToDate] = useState('');
  const [activeTargetsAction, setActiveTargetsAction] = useState('drives');
  const [autoDrivesAppliedKey, setAutoDrivesAppliedKey] = useState(null);
  const [hasAutoSetTrackingStartDate, setHasAutoSetTrackingStartDate] = useState(false);
  const [showBulkTargetModal, setShowBulkTargetModal] = useState(false);
  const [bulkTargetAmount, setBulkTargetAmount] = useState('');
  const [showBulkSubgroupTargetModal, setShowBulkSubgroupTargetModal] = useState(false);
  const [bulkSubgroupTargetAmount, setBulkSubgroupTargetAmount] = useState('');
  const [bulkSubgroupStartDate, setBulkSubgroupStartDate] = useState('');
  const [bulkSubgroupEndDate, setBulkSubgroupEndDate] = useState('');
  const [bulkSubgroupRecurring, setBulkSubgroupRecurring] = useState(false);

  // Initialize local payment routing settings from API data
  useEffect(() => {
    if (paymentRoutingSettings) {
      setLocalPaymentRoutingSettings(JSON.parse(JSON.stringify(paymentRoutingSettings)));
      setHasInteractedPaymentRouting(false);
      setHasAutoSetTrackingStartDate(false);
    }
  }, [paymentRoutingSettings]);

  // When payment routing settings are loaded, prefill the member targets date range
  // from the saved data so the UI reflects EXACT persisted values:
  //  - From Date: the "majority" from_date (most common across member_targets), or tracking_start_date if all are equal
  //  - To Date: latest to_date across all targets.
  useEffect(() => {
    if (!localPaymentRoutingSettings) return;

    const targets = localPaymentRoutingSettings.member_targets || {};
    const targetValues = Object.values(targets);
    if (!targetValues.length) return;

    const fromDates = targetValues
      .map((t) => t?.from_date)
      .filter(Boolean);

    // Compute the majority from_date (mode)
    let majorityFromDate = null;
    if (fromDates.length > 0) {
      const counts = {};
      fromDates.forEach((d) => {
        counts[d] = (counts[d] || 0) + 1;
      });
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]); // desc by count
      const topCount = entries[0][1];
      const topDates = entries
        .filter(([, c]) => c === topCount)
        .map(([d]) => d)
        .sort(); // ISO dates: earliest -> latest
      // If there's a single clear winner, use it; if there's a tie (two only),
      // use the latest of the tied dates.
      majorityFromDate = topDates[topDates.length - 1];
    }

    const toDates = targetValues
      .map((t) => t?.to_date)
      .filter(Boolean)
      .sort(); // latest last

    if (!memberTargetsFromDate && majorityFromDate) {
      setMemberTargetsFromDate(majorityFromDate);
    }

    if (!memberTargetsToDate && toDates.length > 0) {
      setMemberTargetsToDate(toDates[toDates.length - 1]);
    }
  }, [localPaymentRoutingSettings, memberTargetsFromDate, memberTargetsToDate]);

  useEffect(() => {
    if (!localPaymentRoutingSettings) return;

    const targets = localPaymentRoutingSettings.subgroup_targets || {};
    const targetValues = Object.values(targets);
    if (!targetValues.length) return;

    const fromDates = targetValues
      .map((t) => t?.from_date)
      .filter(Boolean);

    let majorityFromDate = null;
    if (fromDates.length > 0) {
      const counts = {};
      fromDates.forEach((d) => {
        counts[d] = (counts[d] || 0) + 1;
      });
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const topCount = entries[0][1];
      const topDates = entries
        .filter(([, c]) => c === topCount)
        .map(([d]) => d)
        .sort();
      majorityFromDate = topDates[topDates.length - 1];
    }

    const toDates = targetValues
      .map((t) => t?.to_date)
      .filter(Boolean)
      .sort();

    if (!subgroupTargetsFromDate && majorityFromDate) {
      setSubgroupTargetsFromDate(majorityFromDate);
    }

    if (!subgroupTargetsToDate && toDates.length > 0) {
      setSubgroupTargetsToDate(toDates[toDates.length - 1]);
    }
  }, [localPaymentRoutingSettings, subgroupTargetsFromDate, subgroupTargetsToDate]);

  // Helper: apply targets from welfare plans for the loaded members
  const applyTargetsFromWelfarePlans = useCallback(async (membersOverride) => {
    const members = membersOverride && membersOverride.length > 0
      ? membersOverride
      : availableMembersForTargets;

    // Nothing to do if we have no members in context
    if (!members || members.length === 0) {
      return;
    }

    try {
      // Auto-populate from active welfare plans
      const response = await api.get('/welfare-plans/active-with-targets');
      if (response.data?.data) {
        const welfarePlans = response.data.data;
        if (welfarePlans.length > 0) {
          const newTargets = { ...(localPaymentRoutingSettings?.member_targets || {}) };
          const cycle = localPaymentRoutingSettings?.contribution_cycle || 'monthly';
          const trackingStart = memberTargetsFromDate || localPaymentRoutingSettings?.tracking_start_date || new Date().toISOString().split('T')[0];
          const toDate = memberTargetsToDate || (() => {
            const endDate = new Date();
            endDate.setFullYear(endDate.getFullYear() + 1);
            return endDate.toISOString().split('T')[0];
          })();

          // Calculate intervals
          const from = new Date(trackingStart);
          const to = new Date(toDate);
          let intervals = 0;
          if (cycle === 'monthly') {
            intervals = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
          } else if (cycle === 'weekly') {
            intervals = Math.ceil((to - from) / (7 * 24 * 60 * 60 * 1000));
          } else {
            intervals = Math.ceil((to - from) / (24 * 60 * 60 * 1000));
          }

          // Use recommended contribution amount if available, otherwise target amount
          const totalWelfareAmount = welfarePlans.reduce((sum, plan) => {
            const amount = parseFloat(plan.recommended_contribution_amount) || parseFloat(plan.target_amount) || 0;
            return sum + amount;
          }, 0);
          const targetAmount = totalWelfareAmount * intervals;
          
          members.forEach(member => {
            const existing = newTargets[member.id];
            // Respect true per-member manual targets (no auto_source flag).
            // Bulk-set targets (auto_source === 'manual_bulk') and previous
            // welfare-based targets (auto_source === 'welfare') CAN be overridden.
            if (existing && !existing.auto_source) {
              return;
            }
            newTargets[member.id] = {
              ...(existing || {}),
              target_amount: targetAmount,
              from_date: trackingStart,
              to_date: toDate,
              auto_source: 'welfare',
            };
          });

          setLocalPaymentRoutingSettings(prev => ({
            ...prev,
            member_targets: newTargets
          }));
          toast.success(`Set welfare plan targets for ${members.length} members`);
        } else {
          toast('No active welfare plans with targets found');
        }
      }
    } catch (error) {
      console.error('Failed to load welfare plans:', error);
      toast.error('Failed to load welfare plans');
    }
  }, [availableMembersForTargets, localPaymentRoutingSettings, memberTargetsFromDate, memberTargetsToDate]);

  // Helper: apply targets from active drives for the loaded members
  const applyTargetsFromActiveDrives = useCallback(async (membersOverride) => {
    const members = membersOverride && membersOverride.length > 0
      ? membersOverride
      : availableMembersForTargets;

    // Nothing to do if we have no members in context
    if (!members || members.length === 0) {
      return;
    }

    try {
      // Auto-populate from active drives
      const response = await api.get('/contribution-drives');
      if (response.data?.data) {
        const activeDrives = response.data.data.filter(d =>
          ['active', 'pending'].includes(d.status) && d.amount_per_member
        );
        if (activeDrives.length > 0) {
          const newTargets = { ...(localPaymentRoutingSettings?.member_targets || {}) };
          const cycle = localPaymentRoutingSettings?.contribution_cycle || 'monthly';
          const trackingStart = memberTargetsFromDate || localPaymentRoutingSettings?.tracking_start_date || new Date().toISOString().split('T')[0];
          const toDate = memberTargetsToDate || (() => {
            const endDate = new Date();
            endDate.setFullYear(endDate.getFullYear() + 1);
            return endDate.toISOString().split('T')[0];
          })();

          // Calculate intervals
          const from = new Date(trackingStart);
          const to = new Date(toDate);
          let intervals = 0;
          if (cycle === 'monthly') {
            intervals = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
          } else if (cycle === 'weekly') {
            intervals = Math.ceil((to - from) / (7 * 24 * 60 * 60 * 1000));
          } else {
            intervals = Math.ceil((to - from) / (24 * 60 * 60 * 1000));
          }

          const totalDriveAmount = activeDrives.reduce((sum, d) => sum + (parseFloat(d.amount_per_member) || 0), 0);
          const targetAmount = totalDriveAmount * intervals;
          
          members.forEach(member => {
            const existing = newTargets[member.id];
            // Respect true per-member manual targets (no auto_source flag).
            // Bulk-set targets (auto_source === 'manual_bulk') and previous
            // drive-based targets (auto_source === 'drives') CAN be overridden.
            if (existing && !existing.auto_source) {
              return;
            }
            newTargets[member.id] = {
              ...(existing || {}),
              target_amount: targetAmount,
              from_date: trackingStart,
              to_date: toDate,
              auto_source: 'drives',
            };
          });

          setLocalPaymentRoutingSettings(prev => ({
            ...prev,
            member_targets: newTargets
          }));
          toast.success(`Set drive‑based targets for ${members.length} members`);
          // Remember that we have already applied drives for this range
          setAutoDrivesAppliedKey(`${trackingStart}|${toDate}`);
        } else {
          toast('No active drives with amounts found');
        }
      }
    } catch (error) {
      toast.error('Failed to load drives');
    }
  }, [availableMembersForTargets, localPaymentRoutingSettings, memberTargetsFromDate, memberTargetsToDate]);

  // Helper: load members for targets
  const loadMembersForTargets = useCallback(async (silent = false) => {
    try {
      // Load all active members
      const response = await api.get('/members');
      if (response.data?.data) {
        const loadedMembers = response.data.data.filter(m => m.is_active);
        setAvailableMembersForTargets(loadedMembers);
        if (!silent) {
          toast.success(`Loaded ${loadedMembers.length} members`);
        }

        // If "From Active Drives" is the active mode, immediately apply those targets too
        if (activeTargetsAction === 'drives' && loadedMembers.length > 0) {
          await applyTargetsFromActiveDrives(loadedMembers);
        }
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to fetch members');
      }
    }
  }, [activeTargetsAction, applyTargetsFromActiveDrives]);

  const loadSubgroupsForTargets = useCallback(async (silent = false) => {
    try {
      const response = await api.get('/subgroups');
      if (response.data?.data) {
        setAvailableSubgroupsForTargets(response.data.data.filter(s => s.type !== 'village'));
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to fetch subgroups');
      }
    }
  }, []);

  // Automatically load members and subgroups when Payment Routing tab is opened
  useEffect(() => {
    if (activeTab === 'payment-routing' && isAdmin) {
      if (availableMembersForTargets.length === 0) {
        loadMembersForTargets(true);
      }
      if (availableSubgroupsForTargets.length === 0) {
        loadSubgroupsForTargets(true);
      }
    }
  }, [
    activeTab,
    isAdmin,
    availableMembersForTargets.length,
    availableSubgroupsForTargets.length,
    loadMembersForTargets,
    loadSubgroupsForTargets,
  ]);

  // Automatically set Payment Tracking Start Date to earliest contribution date
  // when opening the Payment Routing tab, if no tracking date is configured yet.
  useEffect(() => {
    const shouldAutoSet =
      activeTab === 'payment-routing' &&
      isAdmin &&
      localPaymentRoutingSettings &&
      !localPaymentRoutingSettings.tracking_start_date &&
      !hasAutoSetTrackingStartDate;

    if (!shouldAutoSet) {
      return;
    }

    const fetchEarliestDate = async () => {
      try {
        const response = await api.get('/payment-routing/earliest-contribution-date');
        const earliest = response.data?.data?.earliest_date;
        if (earliest) {
          setLocalPaymentRoutingSettings(prev => ({
            ...prev,
            tracking_start_date: earliest,
          }));
        }
      } catch (e) {
        // Non-blocking: if this fails, user can still set the date manually.
      } finally {
        setHasAutoSetTrackingStartDate(true);
      }
    };

    fetchEarliestDate();
  }, [activeTab, isAdmin, localPaymentRoutingSettings, hasAutoSetTrackingStartDate]);

  // Compute dirty state for Payment Routing: enable Save only if there are changes
  const paymentRoutingDirty = (() => {
    if (!localPaymentRoutingSettings || !paymentRoutingSettings) return false;
    if (hasInteractedPaymentRouting) return true;
    try {
      return JSON.stringify(localPaymentRoutingSettings) !== JSON.stringify(paymentRoutingSettings);
    } catch (e) {
      return true;
    }
  })();

  // Group Profile state
  const [groupProfileForm, setGroupProfileForm] = useState({
    name: '',
    description: '',
    is_irregular: false,
    logo: null
  });
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);

  // Prepopulate form from profile
  useEffect(() => {
    if (profile?.data?.mpesa_credentials) {
      const creds = profile.data.mpesa_credentials;
      
      if (!selectedCredentialId && !isCreatingNewAccount) {
        setSelectedCredentialId(creds.id);
        setAccountName(creds.account_name || '');
        setIsDefaultAccount(!!creds.is_default);
      }

      // Set integration type based on is_bank flag
      if (creds.is_bank) {
        setIntegrationType('bank');
      } else if (creds.is_proxy_till) {
        setIntegrationType('proxy');
      } else {
        setIntegrationType('mpesa');
      }
      
      // Populate MPESA form (do not prefill proxy till details into normal till form)
      setMpesaForm({
        shortcode: creds.is_proxy_till ? '' : (creds.shortcode || ''),
        bank_account_reference: creds.account_number || '',
        paybill_number: creds.is_proxy_till ? '' : (creds.paybill_number || ''),
        consumer_key: creds.is_proxy_till ? '' : (creds.consumer_key || ''),
        consumer_secret: creds.is_proxy_till ? '' : (creds.consumer_secret || ''),
        security_credential: creds.is_proxy_till ? '' : (creds.security_credential || ''),
        stk_push_url: creds.stk_push_url || '',
        stk_auth_url: creds.stk_auth_url || '',
        environment: creds.environment || 'production',
        is_active: creds.is_active !== undefined ? creds.is_active : true
      });
      
      // Set bank account fields visibility based on whether BOTH fields have values
      setShowBankAccountFields(!!(creds.paybill_number && creds.account_number));
      
      // Populate Bank form
      setBankForm({
        paybill_number: creds.paybill_number || '',
        account_number: creds.account_number || '',
        is_active: creds.is_active !== undefined ? creds.is_active : true,
      });
      
      // Populate Proxy form
      setProxyForm({
        shortcode: creds.shortcode || '',
      });
    }
  }, [profile]);


  // Prepopulate group profile form
  useEffect(() => {
    if (profile?.data?.current_group) {
      const group = profile.data.current_group;
      setGroupProfileForm({
        name: group.name || '',
        description: group.description || '',
        is_irregular: !!group.is_irregular,
        logo: null
      });
      setLogoError(false); // Reset logo error when profile changes
    }
  }, [profile]);



  // Mutation for updating mpesa credentials
  const mpesaMutation = useMutation({
    mutationFn: async (payload) => {
      setMpesaLoading(true);
      setMpesaError('');
      setMpesaSuccess(false);
      const { data } = await api.post('/mpesa-credentials/update', payload);
      return data;
    },
    onSuccess: () => {
      setMpesaSuccess(true);
      setMpesaLoading(false);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      setMpesaError(error?.response?.data?.message || 'Failed to update MPESA credentials');
      setMpesaLoading(false);
    }
  });

  // Handle MPESA form change
  const handleMpesaChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (!/^\*+$/.test(newValue) && newValue.includes('*')) {
      newValue = newValue.replace(/\*/g, '');
    }
    setMpesaForm((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleBankChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProxyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProxyForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelectCredentialToEdit = (cred) => {
    setSelectedCredentialId(cred.id);
    setIsCreatingNewAccount(false);
    setAccountName(cred.account_name || '');
    setIsDefaultAccount(!!cred.is_default);
    setMpesaFieldErrors({});
    setMpesaError('');
    setBankError('');
    setProxyError('');

    if (cred.is_bank) {
      setIntegrationType('bank');
      setBankForm({
        paybill_number: cred.paybill_number || '',
        account_number: cred.account_number || '',
        is_active: cred.is_active !== undefined ? cred.is_active : true,
      });
    } else if (cred.is_proxy_till) {
      setIntegrationType('proxy');
      setProxyForm({
        shortcode: cred.shortcode || '',
      });
    } else {
      setIntegrationType('mpesa');
      setMpesaForm({
        shortcode: cred.shortcode || '',
        paybill_number: cred.paybill_number || '',
        consumer_key: cred.consumer_key || '',
        consumer_secret: cred.consumer_secret || '',
        security_credential: cred.security_credential || cred.pass_key || '',
        bank_account_reference: cred.account_number || '',
        environment: cred.environment || 'production',
        is_active: cred.is_active !== undefined ? cred.is_active : true,
      });
    }
  };

  const handleStartNewAccount = () => {
    setSelectedCredentialId(null);
    setIsCreatingNewAccount(true);
    setAccountName('');
    setIsDefaultAccount(false);
    setMpesaFieldErrors({});
    setMpesaError('');
    setBankError('');
    setProxyError('');
    setMpesaForm({
      shortcode: '',
      paybill_number: '',
      consumer_key: '',
      consumer_secret: '',
      security_credential: '',
      bank_account_reference: '',
      environment: 'production',
      is_active: true,
    });
    setBankForm({ paybill_number: '', account_number: '', is_active: true });
    setProxyForm({ shortcode: '' });
  };

  const handleSetDefaultCredential = async (id) => {
    try {
      await api.post(`/mpesa-credentials/${id}/set-default`);
      toast.success('Default payment account updated');
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (err) {
      toast.error('Failed to set default credential');
    }
  };

  const handleDeleteCredential = async (id) => {
    if (!window.confirm('Are you sure you want to remove this payment account?')) return;
    try {
      await api.delete(`/mpesa-credentials/${id}`);
      toast.success('Payment account removed');
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (err) {
      toast.error('Failed to delete payment account');
    }
  };

  // Handle MPESA form submit -> Shares API with identifier
  const handleMpesaSubmit = async (e) => {
    e.preventDefault();
    try {
      setMpesaLoading(true);
      setMpesaError('');
      setMpesaFieldErrors({});
      setMpesaSuccess(false);
      await api.post('/mpesa-credentials/update', {
        id: selectedCredentialId,
        is_new: isCreatingNewAccount,
        account_name: accountName,
        is_default: isDefaultAccount,
        is_bank: false,
        shortcode: mpesaForm.shortcode,
        paybill_number: mpesaForm.paybill_number,
        consumer_key: mpesaForm.consumer_key,
        consumer_secret: mpesaForm.consumer_secret,
        security_credential: mpesaForm.security_credential,
        bank_account_reference: mpesaForm.bank_account_reference,
      });
      setMpesaSuccess(true);
      setIsCreatingNewAccount(false);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        const flat = {};
        Object.entries(apiErrors).forEach(([k, v]) => {
          flat[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setMpesaFieldErrors(flat);
      }
      setMpesaError(error?.response?.data?.message || 'Failed to update MPESA settings');
    } finally {
      setMpesaLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setBankLoading(true);
    setBankError('');
    setBankSuccess(false);
    try {
      await api.post('/mpesa-credentials/update', {
        id: selectedCredentialId,
        is_new: isCreatingNewAccount,
        account_name: accountName,
        is_default: isDefaultAccount,
        is_bank: true,
        is_proxy_till: false,
        paybill_number: bankForm.paybill_number,
        account_number: bankForm.account_number,
      });
      setBankSuccess(true);
      setIsCreatingNewAccount(false);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (error) {
      setBankError(error?.response?.data?.message || 'Failed to update bank transfer settings');
    } finally {
      setBankLoading(false);
    }
  };

  const handleProxySubmit = async (e) => {
    e.preventDefault();
    setProxyLoading(true);
    setProxyError('');
    setProxySuccess(false);
    try {
      await api.post('/mpesa-credentials/update', {
        id: selectedCredentialId,
        is_new: isCreatingNewAccount,
        account_name: accountName,
        is_default: isDefaultAccount,
        is_bank: false,
        is_proxy_till: true,
        shortcode: proxyForm.shortcode,
      });
      setProxySuccess(true);
      setIsCreatingNewAccount(false);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (error) {
      setProxyError(error?.response?.data?.message || 'Failed to update proxy settings');
    } finally {
      setProxyLoading(false);
    }
  };

  const saveNotificationChanges = () => {
    // Save all notification settings using the hook
    updateNotificationSettings.mutate({
      'enable_notifications': notifications.enable_notifications,
      'notify_member_of_new_account': notifications.notify_member_of_new_account,
      'can_view_other_members': notifications.can_view_other_members,
      'contribution_reminder': notifications.contribution_reminder,
      'contribution_summary': notifications.contribution_summary,
      'contribution_summary_frequency': notifications.contribution_summary_frequency,
      'contribution_reminder_frequency': notifications.contribution_reminder_frequency,
      'contribution_reminder_schedule_value': notifications.contribution_reminder_schedule_value || null,
      'contribution_reminder_specific_date': notifications.contribution_reminder_specific_date || null
    });
  };

  // Toggle handlers
  const toggleNotification = (setting) => {
    if (!isAdmin) return;
    setNotifications({
      ...notifications,
      [setting]: !notifications[setting]
    });
  };

  const handleNotificationFieldChange = (setting, value) => {
    if (!isAdmin) return;
    setNotifications({
      ...notifications,
      [setting]: value
    });
  };

  const handleContributionReminderFrequencyChange = (value) => {
    if (!isAdmin) return;

    setNotifications((prev) => ({
      ...prev,
      contribution_reminder_frequency: value,
      contribution_reminder_schedule_value:
        value === 'weekly'
          ? (prev.contribution_reminder_schedule_value || 'monday')
          : value === 'monthly'
            ? (prev.contribution_reminder_schedule_value || '1')
            : value === 'yearly'
              ? (prev.contribution_reminder_schedule_value || '01-01')
              : '',
      contribution_reminder_specific_date:
        value === 'specific_date' ? prev.contribution_reminder_specific_date : ''
    }));
  };

  const toggleSecurity = (setting) => {
    setSecurity({
      ...security,
      [setting]: !security[setting]
    });
  };

  // Form handlers
  const handleProfileChange = () => {
    // Profile changes are handled automatically
  };

  const handleAppearanceChange = (setting, value) => {
    setAppearance({
      ...appearance,
      [setting]: value
    });
  };

  // Group Profile handlers
  const handleGroupProfileChange = (e) => {
    const { name, value } = e.target;
    setGroupProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) { // 4MB limit
        toast.error('Logo file size must be less than 4MB');
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, JPG, GIF, or WebP)');
        return;
      }

      setGroupProfileForm(prev => ({
        ...prev,
        logo: file
      }));
      setSelectedLogo(URL.createObjectURL(file));
      setLogoError(false); // Reset error state when new logo is selected
    }
  };

  const handleGroupProfileSubmit = async (e) => {
    if (e) e.preventDefault();
    console.log('Group profile submit handler called');
    
    if (!groupProfileForm.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    // Only include logo if a new one is selected
    const updateData = {
      name: groupProfileForm.name,
      description: groupProfileForm.description,
      is_irregular: groupProfileForm.is_irregular
    };

    if (groupProfileForm.logo) {
      updateData.logo = groupProfileForm.logo;
    }

    try {
      await updateGroupProfile(updateData);
    } catch (error) {
      console.error('Group profile update error:', error);
      // Error is handled by the hook
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container px-6 py-8 mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
        </div>

        <div className="flex flex-col gap-6 md:flex-row">
          {/* Sidebar */}
          <div className="p-4 w-full bg-white rounded-lg shadow md:w-64 settings-nav">
            <nav>
              <ul>
                <li>
                  <button 
                    onClick={() => handleTabChange('profile')}
                    className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'profile' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                  >
                    <User size={20} className="mr-3" />
                    <span>Profile</span>
                  </button>
                </li>
                {isAdmin && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('notifications')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'notifications' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <Bell size={20} className="mr-3" />
                      <span>Notifications</span>
                    </button>
                  </li>
                )}
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => handleTabChange('contribution')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'contribution' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <Clock size={20} className="mr-3" />
                      <span>Contribution</span>
                    </button>
                  </li>
                )}
                {isAdmin && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('general')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'general' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <Settings size={20} className="mr-3" />
                      <span>General Settings</span>
                    </button>
                  </li>
                )}
                {isAdmin && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('sms-quota')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'sms-quota' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <MessageSquare size={20} className="mr-3" />
                      <span>SMS Quota</span>
                    </button>
                  </li>
                )}
                {isAdmin && hasFeature('mpesa_paybill_integration') && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('mpesa')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'mpesa' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <Globe size={20} className="mr-3" />
                      <span>MPESA Integration</span>
                    </button>
                  </li>
                )}
                {isAdmin && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('features')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'features' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <Zap size={20} className="mr-3" />
                      <span>Features</span>
                    </button>
                  </li>
                )}
                {isAdmin && hasFeature('mpesa_paybill_integration') && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('payment-routing')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'payment-routing' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <CreditCard size={20} className="mr-3" />
                      <span>Payment Routing</span>
                    </button>
                  </li>
                )}
                {isAdmin && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('sms-templates')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'sms-templates' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <MessageSquare size={20} className="mr-3" />
                      <span>SMS Templates</span>
                    </button>
                  </li>
                )}
                {isAdmin && hasFeature('advanced_loan_management') && (
                  <li>
                    <button 
                      onClick={() => handleTabChange('loans')}
                      className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'loans' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                    >
                      <DollarSign size={20} className="mr-3" />
                      <span>Loans</span>
                    </button>
                  </li>
                )}
                {/* <li>
                  <button 
                    onClick={() => setActiveTab('security')}
                    className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'security' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                  >
                    <Shield size={20} className="mr-3" />
                    <span>Security & Privacy</span>
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setActiveTab('appearance')}
                    className={`flex items-center px-4 py-2 w-full text-left rounded-md ${activeTab === 'appearance' ? 'bg-green-50 text-green-600' : 'hover:bg-gray-50'}`}
                  >
                    <AlignLeft size={20} className="mr-3" />
                    <span>Appearance</span>
                  </button>
                </li> */}
              </ul>
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 p-6 bg-white rounded-lg shadow">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Profile Settings</h2>
                <form>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                          <User size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={profile?.data?.name}
                          disabled
                          onChange={handleProfileChange}
                          className="block py-2 pl-10 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                          <Phone size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          disabled
                          name="phone"
                          value={profile?.data?.phone_number}
                          onChange={handleProfileChange}
                          className="block py-2 pl-10 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                          <Mail size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          disabled
                          value={profile?.data?.email}
                          onChange={handleProfileChange}
                          className="block py-2 pl-10 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Current Groups
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {profile?.data?.groups?.map((group, index) => (
                          <div
                            key={index}
                            className="inline-block px-3 py-3 mr-2 mb-2 text-sm font-medium text-gray-800 bg-gray-100 rounded-full"
                          > 
                            {group.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3 className="mb-3 font-medium text-gray-700 text-md">WhatsApp Integration</h3>
                    <p className="mb-4 text-sm text-gray-500">
                      Manage your group with whatsapp.
                    </p>
                    <button type="button" className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                    onClick={() => {
                      window.open('https://wa.me/254739264525', '_blank');
                    }}
                    >
                      Connect WhatsApp
                    </button>
                  </div>

                  {/* Group Profile Section - Admin Only */}
                  {isAdmin && (
                    <div className="pt-6 mt-8 border-t">
                      <h3 className="mb-4 font-medium text-gray-700 text-md">Group Profile</h3>
                      <form noValidate>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Group Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={groupProfileForm.name}
                              onChange={handleGroupProfileChange}
                              className="block px-3 py-2 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                              required
                            />
                          </div>

                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Group Logo
                            </label>
                            <div className="flex items-center space-x-4">
                              <div className="flex overflow-hidden justify-center items-center w-16 h-16 bg-gray-100 rounded-full border-2 border-gray-300">
                                {selectedLogo ? (
                                  <img src={selectedLogo} alt="Selected logo" className="object-cover w-full h-full" />
                                ) : profile?.data?.current_group?.logo_full ? (
                                  <img 
                                    src={`${profile.data.current_group.logo_full}`}
                                    alt="Current logo" 
                                    className="object-cover w-full h-full"
                                    onError={() => setLogoError(true)}
                                  />
                                ) : (
                                  <div className="flex justify-center items-center w-full h-full bg-gray-200">
                                    <Image size={24} className="text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleLogoChange}
                                  className="hidden"
                                  id="logo-upload"
                                />
                                <label
                                  htmlFor="logo-upload"
                                  className="px-3 py-1 text-sm text-white bg-blue-600 rounded cursor-pointer hover:bg-blue-700"
                                >
                                  Upload Logo
                                </label>
                                <p className="mt-1 text-xs text-gray-500">
                                  Max 4MB, JPEG, PNG, JPG, GIF, WebP
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Group Description
                            </label>
                            <textarea
                              name="description"
                              value={groupProfileForm.description}
                              onChange={handleGroupProfileChange}
                              rows={3}
                              className="block px-3 py-2 w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                              placeholder="Describe your group..."
                            />
                          </div>

                          <div className="md:col-span-2 flex items-center mt-2 p-4 bg-purple-50 rounded-lg border border-purple-100">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-purple-900 flex items-center">
                                Irregular / Ad-Hoc Contributions Mode
                                {profile?.data?.current_group?.plan !== 'advanced' && (
                                  <span className="ml-2 px-2 py-0.5 text-[10px] font-bold text-white bg-gradient-to-r from-orange-400 to-rose-400 rounded-full shadow-sm">
                                    PRO
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-purple-700 mt-1">
                                Enable this if your group does not have fixed monthly contributions, but instead relies on variable, ad-hoc event drives where members and subgroups can have different targets. This unlocks Subgroups and advanced Drive tools.
                              </p>
                            </div>
                            <div className="ml-4">
                              <button 
                                type="button"
                                onClick={() => {
                                  if (profile?.data?.current_group?.plan !== 'advanced') {
                                    toast.error('This feature is only available on the Advanced plan');
                                    return;
                                  }
                                  setGroupProfileForm(prev => ({ ...prev, is_irregular: !prev.is_irregular }));
                                }}
                                className={profile?.data?.current_group?.plan !== 'advanced' ? 'opacity-50 cursor-not-allowed' : 'transition-transform hover:scale-105'}
                              >
                                {groupProfileForm.is_irregular ? (
                                  <ToggleRight size={32} className="text-purple-600" />
                                ) : (
                                  <ToggleLeft size={32} className="text-gray-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end mt-6">
                          <button
                            type="button"
                            disabled={isUpdatingGroupProfile}
                            onClick={(e) => {
                              e.preventDefault();
                              console.log('Button clicked');
                              handleGroupProfileSubmit(e);
                            }}
                            className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isUpdatingGroupProfile ? (
                              <>
                                <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                                Updating...
                              </>
                            ) : (
                              <>
                                <Save size={18} className="mr-2" />
                                Update Group Profile
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      <Save size={18} className="mr-2" />
                      Save Changes
                    </button>
                  </div> */}
                </form>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && !isProfileLoading && isAdmin && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Notification Settings</h2>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div>
                      <h3 className="font-medium">System notifications</h3>
                      <p className="text-sm text-gray-500">Get alerts when members are added, contributions made, fines issued, loans processed, and more.</p>
                    </div>
                    <button onClick={() => toggleNotification('enable_notifications')}>
                      {notifications.enable_notifications ? (
                        <ToggleRight size={24} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div>
                      <h3 className="font-medium">Welcome notifications</h3>
                      <p className="text-sm text-gray-500">Send welcome messages to new members when they are added to the group.</p>
                    </div>
                    <button onClick={() => toggleNotification('notify_member_of_new_account')}>
                      {notifications.notify_member_of_new_account ? (
                        <ToggleRight size={24} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex justify-end pt-4 mt-6 border-t">
                    <button
                      onClick={() => {saveNotificationChanges()}}
                      type="button"
                      disabled={updateNotificationSettings.isPending}
                      className="flex items-center px-6 py-2 text-white bg-green-600 rounded-md transition-all duration-200 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateNotificationSettings.isPending ? (
                        <>
                          <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={18} className="mr-2" />
                          Save Notification Settings
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Contribution Tab */}
            {activeTab === 'contribution' && !isProfileLoading && isAdmin && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Contribution Settings</h2>
                <p className="mb-6 text-gray-600">Configure contribution reminder timing and summary notifications for members.</p>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div>
                      <h3 className="font-medium">Contribution reminder notifications</h3>
                      <p className="text-sm text-gray-500">Enable reminders and choose when they should be sent.</p>
                    </div>
                    <button onClick={() => toggleNotification('contribution_reminder')}>
                      {notifications.contribution_reminder ? (
                        <ToggleRight size={24} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-md">
                    <label className="block mb-1 text-sm text-gray-600">Reminder frequency</label>
                    <select
                      value={notifications.contribution_reminder_frequency}
                      onChange={(e) => handleContributionReminderFrequencyChange(e.target.value)}
                      disabled={!notifications.contribution_reminder}
                      className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="specific_date">Specific date</option>
                    </select>

                    {notifications.contribution_reminder && notifications.contribution_reminder_frequency === 'weekly' && (
                      <div className="mt-3">
                        <label className="block mb-1 text-sm text-gray-600">Day of week</label>
                        <select
                          value={notifications.contribution_reminder_schedule_value || 'monday'}
                          onChange={(e) => handleNotificationFieldChange('contribution_reminder_schedule_value', e.target.value)}
                          className="px-3 py-2 w-full text-sm rounded-md border border-gray-300"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    )}

                    {notifications.contribution_reminder && notifications.contribution_reminder_frequency === 'monthly' && (
                      <div className="mt-3">
                        <label className="block mb-1 text-sm text-gray-600">Day of month</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={notifications.contribution_reminder_schedule_value || ''}
                          onChange={(e) => handleNotificationFieldChange('contribution_reminder_schedule_value', e.target.value)}
                          className="px-3 py-2 w-full text-sm rounded-md border border-gray-300"
                          placeholder="e.g. 5"
                        />
                      </div>
                    )}

                    {notifications.contribution_reminder && notifications.contribution_reminder_frequency === 'yearly' && (
                      <div className="mt-3">
                        <label className="block mb-1 text-sm text-gray-600">Month and day</label>
                        <input
                          type="date"
                          value={
                            notifications.contribution_reminder_schedule_value
                              ? `${new Date().getFullYear()}-${notifications.contribution_reminder_schedule_value}`
                              : ''
                          }
                          onChange={(e) => {
                            const dateValue = e.target.value;
                            const monthDay = dateValue ? dateValue.slice(5) : '';
                            handleNotificationFieldChange('contribution_reminder_schedule_value', monthDay);
                          }}
                          className="px-3 py-2 w-full text-sm rounded-md border border-gray-300"
                        />
                      </div>
                    )}

                    {notifications.contribution_reminder && notifications.contribution_reminder_frequency === 'specific_date' && (
                      <div className="mt-3">
                        <label className="block mb-1 text-sm text-gray-600">Specific reminder date</label>
                        <input
                          type="date"
                          value={notifications.contribution_reminder_specific_date || ''}
                          onChange={(e) => handleNotificationFieldChange('contribution_reminder_specific_date', e.target.value)}
                          className="px-3 py-2 w-full text-sm rounded-md border border-gray-300"
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-3 bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">Contribution summary notifications</h3>
                        <p className="text-sm text-gray-500">Enable summary updates and choose how often members receive them.</p>
                      </div>
                      <button onClick={() => toggleNotification('contribution_summary')}>
                        {notifications.contribution_summary ? (
                          <ToggleRight size={24} className="text-green-600" />
                        ) : (
                          <ToggleLeft size={24} className="text-gray-400" />
                        )}
                      </button>
                    </div>

                    <div className="mt-3">
                      <label className="block mb-1 text-sm text-gray-600">Summary frequency</label>
                      <select
                        value={notifications.contribution_summary_frequency}
                        onChange={(e) => handleNotificationFieldChange('contribution_summary_frequency', e.target.value)}
                        disabled={!notifications.contribution_summary}
                        className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 mt-6 border-t">
                  <button
                    onClick={() => {saveNotificationChanges()}}
                    type="button"
                    disabled={updateNotificationSettings.isPending}
                    className="flex items-center px-6 py-2 text-white bg-green-600 rounded-md transition-all duration-200 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updateNotificationSettings.isPending ? (
                      <>
                        <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={18} className="mr-2" />
                        Save Contribution Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* General Settings Tab */}
            {activeTab === 'general' && !isProfileLoading && isAdmin && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">General Settings</h2>
                <p className="mb-6">Configure general preferences and privacy settings for your group.</p>

                <div className="space-y-4 bg-grey-50">
                  <div className="flex justify-between items-center p-3 rounded-md border bg-grey-50 border-grey-200">
                    <div>
                      <h3 className="font-medium">👥 Member Directory</h3>
                      <p className="text-sm text-gray-500">Allow members to view and interact with other group members. When enabled, members can see each other's profiles, contact information, and contribution history.</p>
                    </div>
                    <button 
                      onClick={() => toggleNotification('can_view_other_members')}
                      className="transition-transform transform hover:scale-110"
                    >
                      {notifications.can_view_other_members ? (
                        <ToggleRight size={28} className="text-green-600 drop-shadow-sm" />
                      ) : (
                        <ToggleLeft size={28} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex justify-end pt-4 mt-6 border-t">
                    <button
                      onClick={() => {saveNotificationChanges()}}
                      type="button"
                      disabled={updateNotificationSettings.isPending}
                      className="flex items-center px-6 py-2 text-white bg-green-600 rounded-md transition-all duration-200 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateNotificationSettings.isPending ? (
                        <>
                          <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={18} className="mr-2" />
                          Save General Settings
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* MPESA / Bank Integration Tab */}
            {activeTab === 'mpesa' && isAdmin && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">{integrationType === 'mpesa' ? 'MPESA Paybill Integration' : integrationType === 'proxy' ? 'Personal Till Integration' : 'Bank Transfer Integration'}</h2>
                  <div className="flex items-center space-x-3">
                    <label className="text-xl font-semibold text-red-500 text-gray-600">Integration:</label>
                    <select
                      value={integrationType}
                      onChange={(e) => setIntegrationType(e.target.value)}
                      className="px-3 py-2 bg-white rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                    >
                      <option value="mpesa" className="text-lg">MPESA Paybill / Business Till</option>
                      <option value="bank" className="text-lg">Bank Transfer</option>
                      <option value="proxy" className="text-lg">Personal Till Number (with your name)</option>
                    </select>
                  </div>
                </div>

                {/* Configured Payment Accounts List */}
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Configured Payment Accounts</h3>
                      <p className="text-xs text-gray-500">
                        You can connect multiple Paybills, Tills, or Bank accounts to collect funds for different purposes (e.g. Welfare vs. Projects).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleStartNewAccount}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md shadow-sm transition-colors ${
                        isCreatingNewAccount
                          ? 'bg-green-700 text-white ring-2 ring-green-500'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      <Plus size={14} className="mr-1.5" />
                      Add Payment Account
                    </button>
                  </div>

                  {profile?.data?.all_mpesa_credentials && profile.data.all_mpesa_credentials.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {profile.data.all_mpesa_credentials.map((cred) => {
                        const isSelected = selectedCredentialId === cred.id && !isCreatingNewAccount;
                        const channelType = cred.is_bank ? 'Bank Transfer' : cred.is_proxy_till ? 'Personal Till' : 'MPESA Paybill';
                        const channelDetails = cred.is_bank
                          ? `Paybill: ${cred.paybill_number || '—'} | Acc: ${cred.account_number || '—'}`
                          : cred.is_proxy_till
                          ? `Till No: ${cred.shortcode || '—'}`
                          : `Shortcode: ${cred.shortcode || '—'}${cred.paybill_number ? ` (Till: ${cred.paybill_number})` : ''}`;

                        return (
                          <div
                            key={cred.id}
                            className={`p-3 rounded-lg border transition-all ${
                              isSelected
                                ? 'border-green-500 bg-white shadow-sm ring-2 ring-green-100'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="min-w-0">
                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 block">
                                  {channelType}
                                </span>
                                <span className="font-medium text-sm text-gray-900 truncate block">
                                  {cred.account_name || 'Primary Account'}
                                </span>
                              </div>
                              {cred.is_default ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 shrink-0">
                                  <CheckCircle size={10} className="mr-1" />
                                  Default
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleSetDefaultCredential(cred.id)}
                                  className="text-[11px] text-green-600 hover:text-green-800 font-medium shrink-0 hover:underline"
                                >
                                  Make Default
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 truncate mb-3 font-mono">{channelDetails}</p>
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                              <button
                                type="button"
                                onClick={() => handleSelectCredentialToEdit(cred)}
                                className={`inline-flex items-center font-medium ${
                                  isSelected ? 'text-green-700 font-semibold' : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                <Edit size={12} className="mr-1" />
                                {isSelected ? 'Editing' : 'Edit'}
                              </button>
                              {profile.data.all_mpesa_credentials.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCredential(cred.id)}
                                  className="inline-flex items-center text-red-500 hover:text-red-700 font-medium"
                                >
                                  <Trash2 size={12} className="mr-1" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No payment accounts configured yet.</p>
                  )}
                </div>

                {isCreatingNewAccount && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between text-xs text-blue-800">
                    <span><strong>New Account Mode:</strong> Enter the credentials for your new payment channel below.</span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreatingNewAccount(false);
                        const defaultCred = profile?.data?.all_mpesa_credentials?.[0] || profile?.data?.mpesa_credentials;
                        if (defaultCred) handleSelectCredentialToEdit(defaultCred);
                      }}
                      className="text-blue-600 font-semibold hover:underline ml-2"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {/* MPESA Instructions */}
                {integrationType === 'mpesa' && (
                  <>
                    {profile?.data?.mpesa_credentials?.is_proxy_till && (
                      <div className="p-4 mb-6 bg-purple-50 rounded border-l-4 border-purple-400">
                        <p className="text-sm text-purple-800">
                          <strong>Personal Till Active:</strong> Your group currently has Personal Till set up (Till No: <strong>{profile?.data?.mpesa_credentials?.shortcode || proxyForm.shortcode}</strong>).
                          To view or update your Personal Till, select <strong>Personal Till Number (with your name)</strong> in the dropdown above. Fill below only if you want to switch away from Personal Till to custom MPESA Paybill API keys.
                        </p>
                      </div>
                    )}
                    <div className="p-4 mb-6 bg-yellow-50 rounded border-l-4 border-yellow-400">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> With MPESA Paybill / Business Till integration, you only get the ability to receive notifications when money is received to your paybill/till, or to prompt STK push payments for members to your paybill/till. <br/>
                        <span className="block mt-2">We are <strong>not</strong> able to read your paybill balance, send money, or perform any other actions through this integration.</span>
                      </p>
                    </div>
                  </>
                )}

                {/* Bank Transfer Instructions */}
                {integrationType === 'bank' && (
                  <>
                    <div className="p-4 mb-6 bg-green-50 rounded border-l-4 border-green-400">
                      <p className="text-sm text-green-800">
                        <strong>Note:</strong> With Bank Transfer integration, you can provide your Paybill and account number so members can make direct transfers by going to the app and selecting Pay via. <br/>
                      </p>
                    </div>
                    <div className="p-4 mb-6 bg-blue-50 rounded border-l-4 border-blue-400">
                      <p className="mb-2 text-sm text-blue-800">
                        <strong>Bank Transfer Setup Instructions:</strong>
                      </p>
                      <ul className="pl-5 mb-2 text-sm list-disc text-blue-800">
                        <li>Enter your <strong>Paybill Number</strong> - This is the Bank's Paybill</li>
                        <li>Enter your <strong>Account Number</strong> - This is the account number that money will be deposited</li>
                      </ul>
                    </div>
                    <div className="mb-4 text-sm text-center text-gray-500">
                      This integration helps members make payments directly to your Bank with proper reference tracking.
                    </div>
                  </>
                )}

                {/* Proxy Till Instructions */}
                {integrationType === 'proxy' && (
                  <>
                    <div className="p-4 mb-6 bg-purple-50 rounded border-l-4 border-purple-400">
                      <p className="text-sm text-purple-800">
                        <strong>Note:</strong> With Personal Till integration, Twachanga will prompt members via STK Push to pay your Personal Till directly. Funds are routed directly to your Till.
                      </p>
                    </div>
                    <div className="p-4 mb-6 bg-blue-50 rounded border-l-4 border-blue-400">
                      <p className="mb-2 text-sm text-blue-800">
                        <strong>Personal Till Setup Instructions:</strong>
                      </p>
                      <ul className="pl-5 mb-2 text-sm list-disc text-blue-800">
                        <li>Enter your <strong>Till Number</strong> below. No API credentials are required.</li>
                      </ul>
                    </div>
                  </>
                )}

                {integrationType === 'mpesa' && (
                <form onSubmit={handleMpesaSubmit}>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Account Label / Purpose <span className="text-xs text-gray-400 font-normal">(e.g. Welfare Paybill, Main Development Account)</span>
                      </label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="e.g. Welfare Paybill"
                        className="block px-3 py-2 w-full bg-white rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2 flex items-center">
                      <input
                        type="checkbox"
                        id="isDefaultMpesaAccount"
                        checked={isDefaultAccount}
                        onChange={(e) => setIsDefaultAccount(e.target.checked)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isDefaultMpesaAccount" className="ml-2 block text-sm text-gray-700">
                        Set as default payment channel for general group contributions
                      </label>
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Short Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="shortcode"
                        value={mpesaForm.shortcode}
                        onChange={handleMpesaChange}
                        placeholder="e.g. 600988"
                        className={`block px-3 py-2 w-full bg-white rounded-md border shadow-sm focus:ring focus:ring-opacity-50 ${mpesaFieldErrors.shortcode ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
                        required
                      />
                      {mpesaFieldErrors.shortcode && (
                        <p className="mt-1 text-xs text-red-600">{mpesaFieldErrors.shortcode}</p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Paybill / Till Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="paybill_number"
                        value={mpesaForm.paybill_number}
                        onChange={handleMpesaChange}
                        placeholder="e.g. 4017509"
                        className={`block px-3 py-2 w-full bg-white rounded-md border shadow-sm focus:ring focus:ring-opacity-50 ${mpesaFieldErrors.paybill_number ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
                        required
                      />
                      {mpesaFieldErrors.paybill_number && (
                        <p className="mt-1 text-xs text-red-600">{mpesaFieldErrors.paybill_number}</p>
                      )}
                    </div>
                    {[mpesaForm.consumer_key, mpesaForm.consumer_secret, mpesaForm.security_credential].some(v => v?.includes('*')) && (
                      <div className="flex items-center gap-2 p-3 text-sm text-blue-700 bg-blue-50 rounded-md border border-blue-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        <span>Credentials are set and masked (*****). Type a new value to update any key.</span>
                      </div>
                    )}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Consumer Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="consumer_key"
                        value={mpesaForm.consumer_key}
                        onChange={handleMpesaChange}
                        placeholder="Paste your Daraja Consumer Key"
                        className={`block px-3 py-2 w-full bg-white rounded-md border shadow-sm focus:ring focus:ring-opacity-50 ${mpesaFieldErrors.consumer_key ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
                        required
                      />
                      {mpesaFieldErrors.consumer_key && (
                        <p className="mt-1 text-xs text-red-600">{mpesaFieldErrors.consumer_key}</p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Consumer Secret <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="consumer_secret"
                        value={mpesaForm.consumer_secret}
                        onChange={handleMpesaChange}
                        placeholder="Paste your Daraja Consumer Secret"
                        className={`block px-3 py-2 w-full bg-white rounded-md border shadow-sm focus:ring focus:ring-opacity-50 ${mpesaFieldErrors.consumer_secret ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
                        required
                      />
                      {mpesaFieldErrors.consumer_secret && (
                        <p className="mt-1 text-xs text-red-600">{mpesaFieldErrors.consumer_secret}</p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Pass Key <span className="text-red-500">*</span>
                      </label>
                      <p className="mb-2 text-sm text-gray-600">
                        The Pass Key is only needed for <strong>STK Push</strong> payments, where your system sends a payment prompt directly to the customer's phone to enter their M-Pesa PIN.
                        <br /><br />
                        It isn't required for <strong>Customer to Business (C2B)</strong> payments — in that case, customers simply go to <strong>M-Pesa {'>'} Lipa na M-Pesa {'>'} Paybill Number</strong> and enter the Account Number manually.
                      </p>
                      <input
                        type="text"
                        name="security_credential"
                        value={mpesaForm.security_credential}
                        onChange={handleMpesaChange}
                        placeholder="Paste your Daraja Pass Key"
                        className={`block px-3 py-2 w-full bg-white rounded-md border shadow-sm focus:ring focus:ring-opacity-50 ${mpesaFieldErrors.security_credential ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-green-500 focus:ring-green-500'}`}
                        required
                      />
                      {mpesaFieldErrors.security_credential && (
                        <p className="mt-1 text-xs text-red-600">{mpesaFieldErrors.security_credential}</p>
                      )}
                    </div>
                      <div className="flex flex-col p-6 space-y-6 bg-white dark:!bg-transparent rounded-2xl shadow-sm">
                          {/* Header with Toggle */}
                          <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-800">
                                  Send directly to your bank account?
                              </h3>
                              <div className="flex items-center gap-3">
                                  <button
                                      type="button"
                                      onClick={() => setShowBankAccountFields(false)}
                                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                          !showBankAccountFields
                                              ? 'bg-green-600 text-white'
                                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                                      }`}
                                  >
                                      No
                                  </button>
                                  <button
                                      type="button"
                                      onClick={() => setShowBankAccountFields(true)}
                                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                          showBankAccountFields
                                              ? 'bg-green-600 text-white'
                                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                                      }`}
                                  >
                                      Yes
                                  </button>
                              </div>
                          </div>

                          {/* Grid Form - Only show when toggle is Yes */}
                          {showBankAccountFields && (
                              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                  <div>
                                      <label className="block mb-2 text-sm font-medium text-gray-700">
                                          Bank Paybill
                                      </label>
                                      <input
                                          type="text"
                                          name="paybill_number"
                                          value={mpesaForm.paybill_number}
                                          onChange={handleMpesaChange}
                                          className="block px-3 py-2 w-full bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                                      />
                                  </div>

                                  <div>
                                      <label className="block mb-2 text-sm font-medium text-gray-700">
                                          Bank Account Number
                                      </label>
                                      <input
                                          type="text"
                                          name="bank_account_reference"
                                          value={mpesaForm.bank_account_reference}
                                          onChange={handleMpesaChange}
                                          className="block px-3 py-2 w-full bg-gray-50 rounded-lg border border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                                      />
                                  </div>
                              </div>
                          )}
                      </div>

                  </div>
                  {mpesaError && <div className="mt-2 text-sm text-red-600">{mpesaError}</div>}
                  {mpesaSuccess && <div className="mt-2 text-sm text-green-600">MPESA credentials updated successfully!</div>}
                  {mpesaLoading && <div className="mt-2 text-sm text-gray-500">Saving...</div>}
                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                      disabled={mpesaLoading}
                    >
                      <Save size={18} className="mr-2" />
                      {mpesaLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
                )}

                {integrationType === 'proxy' && (
                <form onSubmit={handleProxySubmit}>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Account Label / Purpose <span className="text-xs text-gray-400 font-normal">(e.g. Welfare Till, Treasurer Personal Till)</span>
                      </label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="e.g. Welfare Till"
                        className="block px-3 py-2 w-full bg-white rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2 flex items-center">
                      <input
                        type="checkbox"
                        id="isDefaultProxyAccount"
                        checked={isDefaultAccount}
                        onChange={(e) => setIsDefaultAccount(e.target.checked)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isDefaultProxyAccount" className="ml-2 block text-sm text-gray-700">
                        Set as default payment channel for general group contributions
                      </label>
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Till Number <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="shortcode" value={proxyForm.shortcode} onChange={handleProxyChange} placeholder="e.g. 523411" required className="block px-3 py-2 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50" />
                    </div>
                  </div>
                  
                  {proxyError && <div className="p-3 mt-4 text-sm text-red-600 bg-red-100 rounded-md border border-red-400">{proxyError}</div>}
                  {proxySuccess && <div className="p-3 mt-4 text-sm text-green-600 bg-green-100 rounded-md border border-green-400">Personal Till successfully connected!</div>}
                  
                  <div className="flex justify-end pt-4 mt-6 border-t border-gray-200">
                    <button type="submit" disabled={proxyLoading} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50">
                      {proxyLoading ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
                )}

                {integrationType === 'bank' && (
                  <div className="mt-2">
                    <form onSubmit={handleBankSubmit}>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            Account Label / Purpose <span className="text-xs text-gray-400 font-normal">(e.g. Main Bank Account, Project Bank Account)</span>
                          </label>
                          <input
                            type="text"
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            placeholder="e.g. Welfare Cooperative Bank"
                            className="block px-3 py-2 w-full bg-white rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50 text-sm"
                          />
                        </div>
                        <div className="md:col-span-2 flex items-center">
                          <input
                            type="checkbox"
                            id="isDefaultBankAccount"
                            checked={isDefaultAccount}
                            onChange={(e) => setIsDefaultAccount(e.target.checked)}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <label htmlFor="isDefaultBankAccount" className="ml-2 block text-sm text-gray-700">
                            Set as default payment channel for general group contributions
                          </label>
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Paybill Number <span className="text-red-500">*</span></label>
                          <input type="text" name="paybill_number" value={bankForm.paybill_number} onChange={handleBankChange} placeholder="e.g. 247247" required className="block px-3 py-2 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50" />
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">Account Number <span className="text-red-500">*</span></label>
                          <input type="text" name="account_number" value={bankForm.account_number} onChange={handleBankChange} placeholder="e.g. 1234567890" required className="block px-3 py-2 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50" />
                        </div>
                      </div>
                      {bankError && <div className="mt-2 text-sm text-red-600">{bankError}</div>}
                      {bankSuccess && <div className="mt-2 text-sm text-green-600">Bank transfer settings updated successfully!</div>}
                      <div className="flex justify-end mt-6">
                        <button type="submit" className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700" disabled={bankLoading}>
                          <Save size={18} className="mr-2" />
                          {bankLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && isAdmin && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Features</h2>
                <p className="mb-6">Select which features you want to enable for your group.</p>
                <FeatureSelection />
                <h2 className="mt-10 mb-2 text-xl font-semibold">How your group works</h2>
                <p className="mb-6 text-sm text-gray-600">Turn on extra steps for welfare and meetings. They are off until you switch them on.</p>
                <WorkflowSettings />
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Security & Privacy</h2>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div>
                      <h3 className="font-medium">Two-Factor Authentication</h3>
                      <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                    </div>
                    <button onClick={() => toggleSecurity('twoFactorAuth')}>
                      {security.twoFactorAuth ? (
                        <ToggleRight size={24} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                    <div>
                      <h3 className="font-medium">Share Contact Information</h3>
                      <p className="text-sm text-gray-500">Allow other group members to see your contact details</p>
                    </div>
                    <button onClick={() => toggleSecurity('shareContacts')}>
                      {security.shareContacts ? (
                        <ToggleRight size={24} className="text-green-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="mb-3 font-medium text-gray-700 text-md">Account Security</h3>
                  <div className="space-y-3">
                    <button className="flex items-center font-medium text-blue-600 hover:text-blue-800">
                      Change Password
                    </button>
                    <button className="flex items-center font-medium text-blue-600 hover:text-blue-800">
                      Manage Login Devices
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="mb-3 font-medium text-gray-700 text-md">Data & Privacy</h3>
                  <div className="space-y-3">
                    <button className="flex items-center font-medium text-blue-600 hover:text-blue-800">
                      Download Your Data
                    </button>
                    <button className="flex items-center font-medium text-blue-600 hover:text-blue-800">
                      Delete Account
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                  >
                    <Save size={18} className="mr-2" />
                    Save Settings
                  </button>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">Appearance Settings</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="mb-3 font-medium text-gray-700 text-md">Language</h3>
                    <div className="relative">
                      <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                        <Globe size={18} className="text-gray-400" />
                      </div>
                      <select
                        value={appearance.language}
                        onChange={(e) => handleAppearanceChange('language', e.target.value)}
                        className="block py-2 pl-10 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                      >
                        <option value="English">English</option>
                        <option value="Swahili">Swahili</option>
                        <option value="French">French</option>
                        <option value="Arabic">Arabic</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 font-medium text-gray-700 text-md">Theme</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <button
                        onClick={() => handleAppearanceChange('theme', 'Light')}
                        className={`p-3 rounded-md flex flex-col items-center ${
                          appearance.theme === 'Light' ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="mb-2 w-full h-12 bg-white border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md"></div>
                        <span className="text-sm">Light</span>
                      </button>
                      <button
                        onClick={() => handleAppearanceChange('theme', 'Dark')}
                        className={`p-3 rounded-md flex flex-col items-center ${
                          appearance.theme === 'Dark' ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="mb-2 w-full h-12 bg-gray-800 rounded-md border border-gray-700"></div>
                        <span className="text-sm">Dark</span>
                      </button>
                      <button
                        onClick={() => handleAppearanceChange('theme', 'System')}
                        className={`p-3 rounded-md flex flex-col items-center ${
                          appearance.theme === 'System' ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="mb-2 w-full h-12 bg-gradient-to-r from-white to-gray-800 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md"></div>
                        <span className="text-sm">System</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 font-medium text-gray-700 text-md">Time Zone</h3>
                    <div className="relative">
                      <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                        <Clock size={18} className="text-gray-400" />
                      </div>
                      <select
                        value={appearance.timezone}
                        onChange={(e) => handleAppearanceChange('timezone', e.target.value)}
                        className="block py-2 pl-10 w-full bg-gray-50 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50"
                      >
                        <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                        <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                        <option value="Africa/Cairo">Africa/Cairo (EET)</option>
                        <option value="Europe/London">Europe/London (GMT)</option>
                        <option value="America/New_York">America/New_York (EST)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                  >
                    <Save size={18} className="mr-2" />
                    Save Settings
                  </button>
                </div>
              </div>
            )}

            {/* SMS Quota Tab */}
            {activeTab === 'sms-quota' && isAdmin && (
              <div>
                <h2 className="mb-6 text-xl font-semibold">SMS Quota Management</h2>
                
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* SMS Quota Widget */}
                  <div>
                    <SmsQuotaWidget onRechargeClick={() => setShowRechargeModal(true)} />
                  </div>
                  
                  {/* SMS Recharge History */}
                  <div>
                    <SmsRechargeHistory />
                  </div>
                </div>

                {/* Additional Info */}
                <div className="p-4 mt-8 bg-blue-50 rounded-lg">
                  <h3 className="mb-2 text-lg font-medium text-blue-900">About SMS Quota</h3>
                  <div className="space-y-2 text-sm text-blue-800">
                    <p>• You receive 20 free SMS when you create your first group</p>
                    <p>• SMS are used for sending notifications to group members</p>
                    <p>• Recharge when your quota is low or exhausted</p>
                    <p>• Payments are processed securely via M-Pesa</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Routing Tab */}
            {activeTab === 'payment-routing' && isAdmin && (
              <div>
                {isLoadingPaymentRouting ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                    <span className="ml-2 text-gray-600">Loading payment routing settings...</span>
                  </div>
                ) : (
                  <div>
                    <h2 className="mb-6 text-xl font-semibold">Payment Routing Settings</h2>
                    <p className="mb-6 text-gray-600">
                      Configure how payments are automatically routed to contributions, fines, or loans.
                    </p>
                    
                    <div className="space-y-6">
                  {/* How It Works */}
                  <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="mb-3 text-lg font-medium text-blue-900">How Smart Payment Routing Works</h3>
                    <div className="space-y-2 text-sm text-blue-800">
                      <p><strong>Members just send money</strong> - no need to specify where it should go!</p>
                      <p>The system automatically figures out the best allocation based on your settings below.</p>
                      <p><strong>Example:</strong> Member sends 3,000 KES → System checks: Fines first (500 KES) → Loans second (1,500 KES) → Rest to contributions (1,000 KES)</p>
                    </div>
                  </div>

                  {/* Payment Priority Settings */}
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="mb-4 text-lg font-medium text-gray-900">Payment Priority Order</h3>
                    <p className="mb-4 text-sm text-gray-600">Enable payment types and drag to reorder. Only enabled items will be processed.</p>
                    
                    <div className="space-y-3">
                      {(localPaymentRoutingSettings?.payment_priorities || [])
                        .slice()
                        .sort((a, b) => (Number(a.priority ?? a.order ?? 0)) - (Number(b.priority ?? b.order ?? 0)))
                        .map((priority, index) => (
                        <div 
                          key={priority.type} 
                          className={`flex items-center p-3 bg-white rounded-lg border transition-all ${
                            draggedItem === priority.type ? 'opacity-50 shadow-lg' : ''
                          } ${!priority.enabled ? 'opacity-60' : 'cursor-move'}`}
                          draggable={priority.enabled}
                          onDragStart={(e) => {
                            if (!priority.enabled) {
                              e.preventDefault();
                              return;
                            }
                            setDraggedItem(priority.type);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedItem && draggedItem !== priority.type) {
                              setHasInteractedPaymentRouting(true);
                              const updatedPriorities = (localPaymentRoutingSettings?.payment_priorities || []).map(p => ({ ...p }));
                              const draggedPriority = updatedPriorities.find(p => p.type === draggedItem);
                              const targetPriority = updatedPriorities.find(p => p.type === priority.type);
                              
                              if (draggedPriority && targetPriority) {
                                // Swap priorities
                                const tempPriority = draggedPriority.priority ?? draggedPriority.order ?? 1;
                                draggedPriority.priority = targetPriority.priority ?? targetPriority.order ?? 2;
                                draggedPriority.order = draggedPriority.priority;
                                targetPriority.priority = tempPriority;
                                targetPriority.order = tempPriority;
                                
                                // Sort and reassign sequential priorities
                                updatedPriorities.sort((a, b) => (Number(a.priority ?? a.order ?? 0)) - (Number(b.priority ?? b.order ?? 0)));
                                updatedPriorities.forEach((p, i) => {
                                  p.priority = i + 1;
                                  p.order = i + 1;
                                });
                                
                                setLocalPaymentRoutingSettings({
                                  ...localPaymentRoutingSettings,
                                  payment_priorities: updatedPriorities
                                });
                              }
                            }
                            setDraggedItem(null);
                          }}
                          onDragEnd={() => setDraggedItem(null)}
                        >
                          <div className="flex flex-1 items-center">
                            <div className="flex items-center space-x-3">
                              <GripVertical className={`h-4 w-4 text-gray-400 mr-2 ${!priority.enabled ? 'opacity-30' : ''}`} />
                              <input
                                type="checkbox"
                                checked={priority.enabled}
                                onChange={(e) => {
                                  setHasInteractedPaymentRouting(true);
                                  const updatedPriorities = (localPaymentRoutingSettings?.payment_priorities || []).map(p => ({ ...p }));
                                  const priorityIndex = updatedPriorities.findIndex(p => p.type === priority.type);
                                  if (priorityIndex !== -1) {
                                    updatedPriorities[priorityIndex].enabled = e.target.checked;
                                    
                                    // If enabling, ensure it has a proper priority at the end of enabled items
                                    if (e.target.checked) {
                                      const enabledPriorities = updatedPriorities.filter(p => p.enabled);
                                      updatedPriorities[priorityIndex].priority = enabledPriorities.length;
                                      updatedPriorities[priorityIndex].order = enabledPriorities.length;
                                    }
                                    
                                    // Reassign priorities to be sequential for enabled items
                                    const enabledItems = updatedPriorities.filter(p => p.enabled).sort((a, b) => (Number(a.priority ?? a.order ?? 0)) - (Number(b.priority ?? b.order ?? 0)));
                                    enabledItems.forEach((p, i) => {
                                      p.priority = i + 1;
                                      p.order = i + 1;
                                    });
                                    const disabledItems = updatedPriorities.filter(p => !p.enabled).sort((a, b) => (Number(a.priority ?? a.order ?? 0)) - (Number(b.priority ?? b.order ?? 0)));
                                    let nextIdx = enabledItems.length + 1;
                                    disabledItems.forEach((p) => {
                                      p.priority = nextIdx;
                                      p.order = nextIdx;
                                      nextIdx++;
                                    });
                                    
                                    const updatedSettings = {
                                      ...localPaymentRoutingSettings,
                                      payment_priorities: updatedPriorities
                                    };
                                    setLocalPaymentRoutingSettings(updatedSettings);
                                  }
                                }}
                                className={`h-4 w-4 focus:ring-${priority.color}-500 border-gray-300 rounded`}
                                style={{ color: priority.color === 'red' ? '#dc2626' : priority.color === 'orange' ? '#ea580c' : priority.color === 'blue' ? '#2563eb' : '#16a34a' }}
                              />
                              <span className={`text-sm font-medium ${priority.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                                {priority.type === 'fines' && 'Fines'}
                                {priority.type === 'loans' && 'Loans'}
                                {priority.type === 'contributions' && 'Contributions'}
                                {priority.type === 'drives' && 'Drives'}
                              </span>
                            </div>
                            
                            <div className="flex items-center ml-4 space-x-2">
                              <span className="text-xs text-gray-500">Priority:</span>
                              <span className="text-xs font-medium text-gray-700">
                                {(() => {
                                  if (!priority.enabled) return 'Disabled';
                                  const num = Number(priority.priority ?? priority.order ?? (index + 1));
                                  if (num === 1) return '1st';
                                  if (num === 2) return '2nd';
                                  if (num === 3) return '3rd';
                                  return `${num}th`;
                                })()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="ml-4 text-xs text-gray-500">
                            {priority.type === 'fines' && 'Deduct outstanding fines'}
                            {priority.type === 'loans' && 'Pay loan installments'}
                            {priority.type === 'contributions' && 'Save to contributions'}
                            {priority.type === 'drives' && 'Pay outstanding drive balances (a drive the payer picks is always honoured)'}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-3 mt-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Current Order:</strong> {(localPaymentRoutingSettings?.payment_priorities || [])
                          .slice()
                          .filter(p => p.enabled)
                          .sort((a, b) => (Number(a.priority ?? a.order ?? 0)) - (Number(b.priority ?? b.order ?? 0)))
                          .map(p => p.name)
                          .join(' → ') || 'No payment types enabled'}
                      </p>
                    </div>
                  </div>

                  {/* Contribution Settings */}
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="mb-4 text-lg font-medium text-gray-900">Contribution Settings</h3>
                    
                    <div className="space-y-4">
                      {/* Contribution Cycle */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Contribution Cycle
                        </label>
                        <select
                          value={localPaymentRoutingSettings?.contribution_cycle}
                          onChange={(e) => setLocalPaymentRoutingSettings({
                            ...localPaymentRoutingSettings, 
                            contribution_cycle: e.target.value
                          })}
                          className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="daily">Daily - Every day</option>
                          <option value="weekly">Weekly - Once a week</option>
                          <option value="monthly">Monthly - Once a month</option>
                        </select>
                      </div>

                      {/* Monthly Settings */}
                      {localPaymentRoutingSettings?.contribution_cycle === 'monthly' && (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Monthly Contribution Day
                          </label>
                          <div className="space-y-3">
                            <div className="space-y-3">
                              <div className="flex items-center space-x-3">
                                <label className="text-sm font-medium text-gray-700">Day:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={localPaymentRoutingSettings?.monthly_contribution_day}
                                  onChange={(e) => setLocalPaymentRoutingSettings({
                                    ...localPaymentRoutingSettings, 
                                    monthly_contribution_day: parseInt(e.target.value)
                                  })}
                                  className="px-3 py-2 w-20 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-sm text-gray-600">of month</span>
                              </div>
                              
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={localPaymentRoutingSettings?.monthly_contribution_day === 'last'}
                                  onChange={(e) => setLocalPaymentRoutingSettings({
                                    ...localPaymentRoutingSettings, 
                                    monthly_contribution_day: e.target.checked ? 'last' : 1
                                  })}
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <label className="ml-2 text-sm text-gray-900">
                                  Use last day of month (for months with fewer days)
                                </label>
                              </div>
                            </div>
                            
                            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                              <p className="text-sm text-yellow-800">
                                <strong>Smart Date Handling:</strong>
                                <br/>
                                • If you choose 29th, 30th, or 31st, payments on the last day of shorter months will count as contributions
                                <br/>
                                • Example: If you choose 31st, February payments on 28th/29th will be counted as contributions
                                <br/>
                                • Choose "Last day of month" for automatic last-day processing
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Weekly Settings */}
                      {localPaymentRoutingSettings?.contribution_cycle === 'weekly' && (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Weekly Contribution Day
                          </label>
                          <select
                            value={localPaymentRoutingSettings?.weekly_contribution_day}
                            onChange={(e) => setLocalPaymentRoutingSettings({
                              ...localPaymentRoutingSettings, 
                              weekly_contribution_day: e.target.value
                            })}
                            className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="monday">Monday</option>
                            <option value="tuesday">Tuesday</option>
                            <option value="wednesday">Wednesday</option>
                            <option value="thursday">Thursday</option>
                            <option value="friday">Friday</option>
                            <option value="saturday">Saturday</option>
                            <option value="sunday">Sunday</option>
                          </select>
                        </div>
                      )}

                      {/* Daily Settings */}
                      {localPaymentRoutingSettings?.contribution_cycle === 'daily' && (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Daily Contribution Time
                          </label>
                          <input
                            type="time"
                            value={localPaymentRoutingSettings?.daily_contribution_time}
                            onChange={(e) => setLocalPaymentRoutingSettings({
                              ...localPaymentRoutingSettings, 
                                  daily_contribution_time: e.target.value
                            })}
                            className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">Payments received after this time will be counted for the next day</p>
                        </div>
                      )}
                      
                      {/* Contribution Amount - Hidden/Deprecated - Now using member targets */}
                      <div className="hidden">
                        <input
                          type="number"
                          value={localPaymentRoutingSettings?.contribution_amount || 0}
                          readOnly
                        />
                      </div>
                      
                      {Number(profile?.data?.current_group?.is_house_management) !== 1 && (
                        <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
                          <p className="text-sm font-medium text-yellow-800 mb-2">
                            ⚠️ Contribution Amount Setting
                          </p>
                          <p className="text-xs text-yellow-700">
                            The contribution amount is now managed through <strong>Member Targets</strong> below. 
                            Set individual targets for each member with date ranges, and the system will automatically calculate 
                            expected amounts per {localPaymentRoutingSettings?.contribution_cycle === 'daily' ? 'day' : 
                            localPaymentRoutingSettings?.contribution_cycle === 'weekly' ? 'week' : 'month'} based on the intervals in the date range.
                          </p>
                        </div>
                      )}

                      {/* Payment Tracking Start Date - Now visible */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Payment Tracking Start Date
                        </label>
                        <input
                          type="date"
                          value={localPaymentRoutingSettings?.tracking_start_date || ''}
                          onChange={(e) => setLocalPaymentRoutingSettings({
                            ...localPaymentRoutingSettings,
                            tracking_start_date: e.target.value
                          })}
                          className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Only contributions from this date onwards are used for payment tracking and balance calculations.
                        </p>
                      </div>

                      {/* Targets Section */}
                      {/* ── HOUSE MANAGEMENT: show house/subgroup targets only ── */}
                      {Number(profile?.data?.current_group?.is_house_management) === 1 && (() => {
                        const houseLabel = profile?.data?.current_group?.house_label || 'House';
                        return (
                          <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                            <h4 className="mb-3 text-lg font-semibold text-gray-900">{houseLabel} Contribution Targets</h4>
                            <div className="mb-4 p-4 bg-blue-50 rounded-md border border-blue-200 text-left">
                              <p className="text-sm text-blue-800 font-medium mb-2">How it works:</p>
                              <ol className="text-xs text-blue-700 list-decimal list-inside space-y-1">
                                <li>Set a <strong>target amount</strong> (total for the period) and target date range for each {houseLabel.toLowerCase()}</li>
                                <li>The system sums up occupant contributions inside that {houseLabel.toLowerCase()} during that period to compute progress</li>
                              </ol>
                            </div>

                            {/* Bulk Target Modal for Subgroups */}
                            <Modal
                              isOpen={showBulkSubgroupTargetModal}
                              onClose={() => {
                                setShowBulkSubgroupTargetModal(false);
                                setBulkSubgroupTargetAmount('');
                                setBulkSubgroupRecurring(false);
                              }}
                              title={`Set Target for All ${houseLabel}s`}
                              size="sm"
                            >
                              <div className="space-y-4 text-left">
                                <p className="text-sm text-gray-700">
                                  Enter the target configuration to apply to <strong>every {houseLabel.toLowerCase()}</strong>:
                                </p>

                                <label className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-md cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={bulkSubgroupRecurring}
                                    onChange={(e) => setBulkSubgroupRecurring(e.target.checked)}
                                    className="mt-0.5 rounded border-gray-300"
                                  />
                                  <span className="text-xs text-blue-900">
                                    <strong>Monthly recurring</strong> — the amount below is charged each calendar month. Overpayments still pre-pay future months.
                                  </span>
                                </label>

                                <div>
                                  <label className="block mb-1 text-xs font-medium text-gray-700">
                                    {bulkSubgroupRecurring
                                      ? `Per-Month Amount per ${houseLabel} (KES)`
                                      : `Target Amount per ${houseLabel} (KES)`}
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="50"
                                    value={bulkSubgroupTargetAmount}
                                    onChange={(e) => setBulkSubgroupTargetAmount(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={bulkSubgroupRecurring ? 'e.g. 5000' : 'e.g. 60000'}
                                  />
                                </div>
                                <div>
                                  <label className="block mb-1 text-xs font-medium text-gray-700">
                                    Start Date
                                  </label>
                                  <input
                                    type="date"
                                    value={bulkSubgroupStartDate}
                                    onChange={(e) => setBulkSubgroupStartDate(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block mb-1 text-xs font-medium text-gray-700">
                                    End Date{bulkSubgroupRecurring && <span className="text-gray-400 font-normal"> (optional)</span>}
                                  </label>
                                  <input
                                    type="date"
                                    value={bulkSubgroupEndDate}
                                    onChange={(e) => setBulkSubgroupEndDate(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div className="flex justify-end space-x-2 mt-4">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowBulkSubgroupTargetModal(false);
                                      setBulkSubgroupTargetAmount('');
                                      setBulkSubgroupRecurring(false);
                                    }}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const parsedAmount = bulkSubgroupTargetAmount ? parseFloat(bulkSubgroupTargetAmount) : NaN;
                                      if (isNaN(parsedAmount) || parsedAmount <= 0) {
                                        toast.error('Please enter a valid amount');
                                        return;
                                      }
                                      if (!bulkSubgroupStartDate) {
                                        toast.error('Please select a start date');
                                        return;
                                      }
                                      // End date is required for period mode; optional for recurring.
                                      if (!bulkSubgroupRecurring && !bulkSubgroupEndDate) {
                                        toast.error('Please select an end date');
                                        return;
                                      }
                                      if (bulkSubgroupEndDate && bulkSubgroupStartDate > bulkSubgroupEndDate) {
                                        toast.error('Start date cannot be after end date');
                                        return;
                                      }

                                      if (availableSubgroupsForTargets.length === 0) {
                                        toast.error(`Please make sure you have created ${houseLabel.toLowerCase()}s first`);
                                        return;
                                      }

                                      const newTargets = { ...(localPaymentRoutingSettings?.subgroup_targets || {}) };
                                      availableSubgroupsForTargets.forEach(subgroup => {
                                        newTargets[subgroup.id] = {
                                          mode: bulkSubgroupRecurring ? 'monthly_recurring' : 'period',
                                          target_amount: parsedAmount,
                                          from_date: bulkSubgroupStartDate,
                                          to_date: bulkSubgroupEndDate || ''
                                        };
                                      });

                                      setLocalPaymentRoutingSettings({
                                        ...localPaymentRoutingSettings,
                                        subgroup_targets: newTargets,
                                      });

                                      setSubgroupTargetsFromDate(bulkSubgroupStartDate);
                                      setSubgroupTargetsToDate(bulkSubgroupEndDate);

                                      const what = bulkSubgroupRecurring
                                        ? `recurring KSh ${parsedAmount.toLocaleString()}/month`
                                        : `KSh ${parsedAmount.toLocaleString()} target`;
                                      const period = bulkSubgroupEndDate
                                        ? `${bulkSubgroupStartDate} to ${bulkSubgroupEndDate}`
                                        : `from ${bulkSubgroupStartDate}`;
                                      toast.success(`Set ${what} (${period}) for all ${availableSubgroupsForTargets.length} ${houseLabel.toLowerCase()}(s)`);
                                      setShowBulkSubgroupTargetModal(false);
                                      setBulkSubgroupTargetAmount('');
                                      setBulkSubgroupRecurring(false);
                                    }}
                                    className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                  >
                                    Apply Targets
                                  </button>
                                </div>
                              </div>
                            </Modal>

                            {/* "Set Target for All Houses" button */}
                            <div className="mb-4 text-left">
                              <button
                                type="button"
                                onClick={() => {
                                  // Preload from existing targets if they're all the same; otherwise blank.
                                  const targets = localPaymentRoutingSettings?.subgroup_targets || {};
                                  const entries = Object.values(targets);
                                  const allObj = entries.length > 0 && entries.every(t => t && typeof t === 'object');
                                  const sameAmount = allObj && entries.every(t => Number(t.target_amount) === Number(entries[0].target_amount));
                                  const sameFrom = allObj && entries.every(t => (t.from_date || '') === (entries[0].from_date || ''));
                                  const sameTo = allObj && entries.every(t => (t.to_date || '') === (entries[0].to_date || ''));
                                  const sameMode = allObj && entries.every(t => (t.mode || 'period') === (entries[0].mode || 'period'));

                                  if (sameAmount && sameFrom && sameTo && sameMode && entries.length > 0) {
                                    setBulkSubgroupTargetAmount(entries[0].target_amount || '');
                                    setBulkSubgroupStartDate(entries[0].from_date || subgroupTargetsFromDate || '');
                                    setBulkSubgroupEndDate(entries[0].to_date || subgroupTargetsToDate || '');
                                    setBulkSubgroupRecurring((entries[0].mode || 'period') === 'monthly_recurring');
                                  } else {
                                    setBulkSubgroupTargetAmount('');
                                    setBulkSubgroupStartDate(subgroupTargetsFromDate || '');
                                    setBulkSubgroupEndDate(subgroupTargetsToDate || '');
                                    setBulkSubgroupRecurring(false);
                                  }
                                  setShowBulkSubgroupTargetModal(true);
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-all"
                              >
                                Set Target for All {houseLabel}s
                              </button>
                            </div>

                            {/* Subgroup List */}
                            <div className="mt-4">
                              <h5 className="text-sm font-medium text-gray-900 mb-3 text-left">Set {houseLabel} Targets</h5>
                              
                              {availableSubgroupsForTargets.length === 0 ? (
                                <div className="p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
                                  <p className="text-sm text-gray-600">No {houseLabel.toLowerCase()}s found. Create {houseLabel.toLowerCase()}s under the {houseLabel}s Management section first.</p>
                                </div>
                              ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {availableSubgroupsForTargets.map((subgroup) => {
                                    const currentTarget = localPaymentRoutingSettings?.subgroup_targets?.[subgroup.id];
                                    const targetAmount = typeof currentTarget === 'number'
                                      ? currentTarget
                                      : (currentTarget && typeof currentTarget === 'object' ? currentTarget?.target_amount : '');
                                    const fromDate = currentTarget && typeof currentTarget === 'object' ? currentTarget?.from_date : '';
                                    const toDate = currentTarget && typeof currentTarget === 'object' ? currentTarget?.to_date : '';
                                    const isRecurring = currentTarget && typeof currentTarget === 'object' && currentTarget?.mode === 'monthly_recurring';

                                    const updateTargetField = (patch) => {
                                      const current = localPaymentRoutingSettings?.subgroup_targets?.[subgroup.id];
                                      const base = (current && typeof current === 'object')
                                        ? current
                                        : { target_amount: Number(current) || 0, from_date: subgroupTargetsFromDate || '', to_date: subgroupTargetsToDate || '', mode: 'period' };
                                      setLocalPaymentRoutingSettings({
                                        ...localPaymentRoutingSettings,
                                        subgroup_targets: {
                                          ...(localPaymentRoutingSettings?.subgroup_targets || {}),
                                          [subgroup.id]: { ...base, ...patch },
                                        }
                                      });
                                    };

                                    return (
                                      <div key={subgroup.id} className="p-3 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md text-left">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">{subgroup.name}</div>
                                            <div className="text-xs text-gray-500">{subgroup.description || 'No description'}</div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 cursor-pointer select-none">
                                              <input
                                                type="checkbox"
                                                checked={!!isRecurring}
                                                onChange={(e) => updateTargetField({ mode: e.target.checked ? 'monthly_recurring' : 'period' })}
                                                className="rounded border-gray-300"
                                              />
                                              Monthly recurring
                                            </label>
                                            {(targetAmount || fromDate || toDate) && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newTargets = { ...(localPaymentRoutingSettings?.subgroup_targets || {}) };
                                                  delete newTargets[subgroup.id];
                                                  setLocalPaymentRoutingSettings({
                                                    ...localPaymentRoutingSettings,
                                                    subgroup_targets: Object.keys(newTargets).length > 0 ? newTargets : null
                                                  });
                                                }}
                                                className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
                                              >
                                                Clear
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                          <div>
                                            <label className="block mb-1 text-xs font-medium text-gray-700">
                                              {isRecurring ? 'Per-Month Amount (KES)' : 'Target Amount (KES)'}
                                            </label>
                                            <input
                                              type="number"
                                              placeholder={isRecurring ? 'e.g. 5000 per month' : 'Enter total target'}
                                              value={targetAmount}
                                              onChange={(e) => {
                                                const value = e.target.value ? parseFloat(e.target.value) : '';
                                                updateTargetField({ target_amount: value });
                                              }}
                                              className="px-2 py-1 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block mb-1 text-xs font-medium text-gray-700">Start Date</label>
                                            <input
                                              type="date"
                                              value={currentTarget ? (fromDate || '') : (subgroupTargetsFromDate || '')}
                                              onChange={(e) => updateTargetField({ from_date: e.target.value })}
                                              className="px-2 py-1 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block mb-1 text-xs font-medium text-gray-700">
                                              {isRecurring ? 'End Date (optional)' : 'End Date'}
                                            </label>
                                            <input
                                              type="date"
                                              value={currentTarget ? (toDate || '') : (subgroupTargetsToDate || '')}
                                              onChange={(e) => updateTargetField({ to_date: e.target.value })}
                                              className="px-2 py-1 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                          </div>
                                        </div>
                                        {isRecurring && (
                                          <p className="mt-2 text-[11px] text-gray-500">
                                            Recurring: amount above is charged each calendar month. Overpayments still pre-pay future months.
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {localPaymentRoutingSettings?.subgroup_targets && Object.keys(localPaymentRoutingSettings.subgroup_targets).length > 0 && (
                              <div className="mt-3 p-2 text-xs bg-blue-50 rounded-md text-blue-800 text-left">
                                {Object.keys(localPaymentRoutingSettings.subgroup_targets).length} {houseLabel.toLowerCase()}(s) have targets set
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ── REGULAR GROUPS: show per-member targets only ── */}
                      {Number(profile?.data?.current_group?.is_house_management) !== 1 && (
                        <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                          <h4 className="mb-3 text-lg font-semibold text-gray-900">Member Contribution Targets</h4>
                          <div className="mb-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                            <p className="text-sm text-blue-800 font-medium mb-2">How it works:</p>
                            <ol className="text-xs text-blue-700 list-decimal list-inside space-y-1">
                              <li>Set a <strong>target amount</strong> (total for the period) for each member</li>
                              <li>The system calculates: <strong>Expected per {localPaymentRoutingSettings?.contribution_cycle === 'daily' ? 'day' : 
                                localPaymentRoutingSettings?.contribution_cycle === 'weekly' ? 'week' : 'month'} = Target Amount ÷ Number of Intervals</strong></li>
                              <li>Example: 12,000 KES target from Jan 1 to Dec 31 (12 months) = 1,000 KES per month</li>
                            </ol>
                          </div>

                          {/* Bulk Target Modal */}
                          <Modal
                            isOpen={showBulkTargetModal}
                            onClose={() => {
                              setShowBulkTargetModal(false);
                              setBulkTargetAmount('');
                            }}
                            title="Set Target for All Members"
                            size="sm"
                          >
                            <div className="space-y-4">
                              <p className="text-sm text-gray-700">
                                Enter a total target amount (KES) for <strong>each member</strong>:
                              </p>
                              <div>
                                <label className="block mb-1 text-xs font-medium text-gray-700">
                                  Target Amount per Member (KES)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="50"
                                  value={bulkTargetAmount}
                                  onChange={(e) => setBulkTargetAmount(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="e.g. 12000"
                                />
                              </div>
                              <div className="flex justify-end space-x-2 mt-4">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowBulkTargetModal(false);
                                    setBulkTargetAmount('');
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const parsed = bulkTargetAmount ? parseFloat(bulkTargetAmount) : NaN;
                                    if (!parsed || parsed <= 0) {
                                      toast.error('Please enter a valid amount');
                                      return;
                                    }
                                    const newTargets = { ...(localPaymentRoutingSettings?.member_targets || {}) };

                                    if (availableMembersForTargets.length === 0) {
                                      toast('Please load members first');
                                      return;
                                    }

                                    availableMembersForTargets.forEach(member => {
                                      newTargets[member.id] = parsed;
                                    });

                                    setLocalPaymentRoutingSettings({
                                      ...localPaymentRoutingSettings,
                                      member_targets: newTargets,
                                    });
                                    toast.success(`Set target of ${parsed.toLocaleString()} KES for ${availableMembersForTargets.length} members`);
                                    setShowBulkTargetModal(false);
                                    setBulkTargetAmount('');
                                  }}
                                  className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                  Apply Targets
                                </button>
                              </div>
                            </div>
                          </Modal>
                          
                          {availableMembersForTargets.length > 0 && (
                            <div className="flex items-center text-xs text-green-700 bg-green-50 px-3 py-2 rounded-md border border-green-200 mb-4">
                              <CheckCircle size={14} className="mr-1.5" />
                              {availableMembersForTargets.length} members loaded
                            </div>
                          )}

                          {/* Quick Actions */}
                          <div className="mb-4">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveTargetsAction('manual');
                                setShowBulkTargetModal(true);
                              }}
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-all"
                            >
                              Set Target for All
                            </button>
                          </div>
                          
                          {/* Warning about balance calculation */}
                          <div className="mb-4 p-3 bg-red-50 dark:bg-red-90/5 rounded-md border border-red-200 dark:border-red-800">
                            <p className="text-xs font-medium text-red-800 dark:text-red-200 mb-1">⚠️ Balance Calculation Warning:</p>
                            <p className="text-xs text-red-700 dark:text-red-300">
                              SMS messages with balance placeholders (
                              <code className="bg-red-100 dark:bg-red-900/50 px-1.5 py-0.5 rounded">
                                {'{balance}'}
                              </code>
                              ) will only be sent if members have valid targets set or active contribution 
                              drives. If a member doesn't have a target configured, balance placeholders 
                              will be removed from SMS messages to prevent confusion.
                            </p>
                          </div>

                          {/* Member List */}
                          <div className="mt-4">
                            <h5 className="text-sm font-medium text-gray-900 mb-3">Set Individual Targets</h5>
                            
                            {availableMembersForTargets.length === 0 ? (
                              <div className="p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
                                <p className="text-sm text-gray-600">Loading members...</p>
                              </div>
                            ) : (
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {availableMembersForTargets.map((member) => {
                                  const currentTarget = localPaymentRoutingSettings?.member_targets?.[member.id];
                                  const targetAmount = typeof currentTarget === 'number' ? currentTarget : (currentTarget?.target_amount || '');
                                  
                                  return (
                                    <div key={member.id} className="p-3 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-gray-900">{member.name}</div>
                                          <div className="text-xs text-gray-500">{member.phone_number}</div>
                                        </div>
                                        {targetAmount && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newTargets = { ...(localPaymentRoutingSettings?.member_targets || {}) };
                                              delete newTargets[member.id];
                                              setLocalPaymentRoutingSettings({
                                                ...localPaymentRoutingSettings,
                                                member_targets: Object.keys(newTargets).length > 0 ? newTargets : null
                                              });
                                            }}
                                            className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                      <div>
                                        <label className="block mb-1 text-xs font-medium text-gray-700">Target Amount (KES)</label>
                                        <input
                                          type="number"
                                          placeholder="Enter target amount"
                                          value={targetAmount}
                                          onChange={(e) => {
                                            const value = e.target.value ? parseFloat(e.target.value) : '';
                                            setLocalPaymentRoutingSettings({
                                              ...localPaymentRoutingSettings,
                                              member_targets: {
                                                ...(localPaymentRoutingSettings?.member_targets || {}),
                                                [member.id]: value
                                              }
                                            });
                                          }}
                                          className="px-2 py-1 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {localPaymentRoutingSettings?.member_targets && Object.keys(localPaymentRoutingSettings.member_targets).length > 0 && (
                            <div className="mt-3 p-2 text-xs bg-blue-50 rounded-md text-blue-800">
                              {Object.keys(localPaymentRoutingSettings.member_targets).length} member(s) have targets set
                            </div>
                          )}
                        </div>
                      )}

                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={localPaymentRoutingSettings?.allow_partial_contributions}
                          onChange={(e) => setLocalPaymentRoutingSettings({
                            ...localPaymentRoutingSettings, 
                            allow_partial_contributions: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <label className="ml-2 text-sm text-gray-900">
                          Allow partial contributions (any amount)
                        </label>
                      </div>
                      
                      {/* Automatic Fines for Missed Contributions */}
                      <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                        <h4 className="mb-3 font-medium text-gray-900">Automatic Fines for Missed Contributions</h4>
                        
                        <div className="space-y-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={localPaymentRoutingSettings?.auto_fines_enabled}
                              onChange={(e) => setLocalPaymentRoutingSettings({
                                ...localPaymentRoutingSettings, 
                                auto_fines_enabled: e.target.checked
                              })}
                              className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-900">
                              Automatically fine members for missed contributions
                            </label>
                          </div>
                          
                          {localPaymentRoutingSettings?.auto_fines_enabled && (
                            <div className="pl-6 space-y-3">
                              <div className="flex items-center space-x-3">
                                <label className="text-sm font-medium text-gray-700">Grace Period:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="30"
                                  value={localPaymentRoutingSettings?.grace_period_days}
                                  onChange={(e) => setLocalPaymentRoutingSettings({
                                    ...localPaymentRoutingSettings, 
                                    grace_period_days: parseInt(e.target.value)
                                  })}
                                  className="px-3 py-2 w-20 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-sm text-gray-600">days after due date</span>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <label className="text-sm font-medium text-gray-700">Fine Amount:</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="50"
                                  value={localPaymentRoutingSettings?.fine_amount}
                                  onChange={(e) => setLocalPaymentRoutingSettings({
                                    ...localPaymentRoutingSettings, 
                                    fine_amount: parseInt(e.target.value)
                                  })}
                                  className="px-3 py-2 w-24 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-sm text-gray-600">KES</span>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <label className="text-sm font-medium text-gray-700">Max Fines:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={localPaymentRoutingSettings?.max_fines_per_month}
                                  onChange={(e) => setLocalPaymentRoutingSettings({
                                    ...localPaymentRoutingSettings, 
                                    max_fines_per_month: parseInt(e.target.value)
                                  })}
                                  className="px-3 py-2 w-20 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-sm text-gray-600">per period</span>
                              </div>
                              
                              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                <p className="text-sm text-red-800">
                                  <strong>Example:</strong> If contribution is due on 6th, grace period is 7 days, and fine is 200 KES:
                                  <br/>
                                  • Member pays by 13th → No fine
                                  <br/>
                                  • Member pays after 13th → 200 KES fine automatically added
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>


                </div>
                
                {/* Notification Settings */}
                <div className="p-6 mt-6 bg-gray-50 rounded-lg">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">Notification Settings</h3>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={localPaymentRoutingSettings?.notify_admin_on_payment}
                        onChange={(e) => setLocalPaymentRoutingSettings({
                          ...localPaymentRoutingSettings, 
                          notify_admin_on_payment: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <label className="ml-2 text-sm text-gray-900">
                        Notify admin when payment is received
                      </label>
                    </div>
                    
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={localPaymentRoutingSettings?.notify_member_on_payment}
                        onChange={(e) => setLocalPaymentRoutingSettings({
                          ...localPaymentRoutingSettings, 
                          notify_member_on_payment: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <label className="ml-2 text-sm text-gray-900">
                        Notify member on payment confirmation
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Payment Reminder Settings */}
                <div className="p-6 mt-6 bg-gray-50 rounded-lg">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">Payment Reminder Settings</h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Configure automated reminders for upcoming and overdue payments. Reminders are sent via SMS and require SMS quota.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* Due Reminders */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                          <input
                            type="checkbox"
                            checked={localPaymentRoutingSettings?.due_reminders_enabled ?? true}
                            onChange={(e) => setLocalPaymentRoutingSettings({
                              ...localPaymentRoutingSettings, 
                              due_reminders_enabled: e.target.checked
                            })}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label className="ml-2 text-sm font-medium text-gray-900">
                            Enable due payment reminders
                          </label>
                        </div>
                        
                        {localPaymentRoutingSettings?.due_reminders_enabled !== false && (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Days before due date
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="30"
                              value={localPaymentRoutingSettings?.reminder_days_before_due ?? 1}
                              onChange={(e) => setLocalPaymentRoutingSettings({
                                ...localPaymentRoutingSettings, 
                                reminder_days_before_due: parseInt(e.target.value) || 1
                              })}
                              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Send reminder X days before payment is due
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Late Reminders */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                          <input
                            type="checkbox"
                            checked={localPaymentRoutingSettings?.late_reminders_enabled ?? true}
                            onChange={(e) => setLocalPaymentRoutingSettings({
                              ...localPaymentRoutingSettings, 
                              late_reminders_enabled: e.target.checked
                            })}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label className="ml-2 text-sm font-medium text-gray-900">
                            Enable late payment reminders
                          </label>
                        </div>
                        
                        {localPaymentRoutingSettings?.late_reminders_enabled !== false && (
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Late reminder frequency
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={localPaymentRoutingSettings?.late_reminder_frequency_days ?? 3}
                              onChange={(e) => setLocalPaymentRoutingSettings({
                                ...localPaymentRoutingSettings, 
                                late_reminder_frequency_days: parseInt(e.target.value) || 3
                              })}
                              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              How often to send late reminders (e.g., 3 = every 3 days)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-4 mt-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Reminder message templates are now editable on the <strong>SMS Templates</strong> tab. 
                        You can customize the Due Reminder and Late Reminder messages there. 
                        The settings above control when reminders are sent.
                      </p>
                    </div>
                  </div>
                </div>
                
                                  {/* Smart Routing Examples */}
                  <div className="mt-6">
                    <h3 className="mb-4 text-lg font-medium text-gray-900">Smart Routing Examples</h3>
                  
                                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="mb-2 font-medium text-green-900">Example 1: Contribution Payment</h4>
                        <p className="text-sm text-green-800">
                          <strong>When:</strong> {localPaymentRoutingSettings?.contribution_cycle === 'daily' && 'Daily at 9:00 AM, member sends 100 KES'}
                          {localPaymentRoutingSettings?.contribution_cycle === 'weekly' && 'Every Monday, member sends 500 KES'}
                          {localPaymentRoutingSettings?.contribution_cycle === 'monthly' && '1st of month, member sends 1,000 KES'}<br/>
                          <strong>Result:</strong> Amount → Contributions<br/>
                          <strong>Why:</strong> Matches contribution cycle and amount
                        </p>
                      </div>
                    
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="mb-2 font-medium text-orange-900">Example 2: Loan Repayment</h4>
                      <p className="text-sm text-orange-800">
                        <strong>When:</strong> Member has 2,000 KES loan, sends 3,000 KES<br/>
                        <strong>Result:</strong> 2,000 KES → Loans, 1,000 KES → Contributions<br/>
                        <strong>Why:</strong> Loans deducted first, rest to contributions
                      </p>
                    </div>
                    
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="mb-2 font-medium text-red-900">Example 3: Fine Payment</h4>
                      <p className="text-sm text-red-800">
                        <strong>When:</strong> Member has 500 KES fine, sends 1,500 KES<br/>
                        <strong>Result:</strong> 500 KES → Fines, 1,000 KES → Contributions<br/>
                        <strong>Why:</strong> Fines deducted first, rest to contributions
                      </p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="mb-2 font-medium text-blue-900">Example 4: Complex Payment</h4>
                      <p className="text-sm text-blue-800">
                        <strong>When:</strong> Member has 300 KES fine + 1,500 KES loan, sends 3,000 KES<br/>
                        <strong>Result:</strong> 300 KES → Fines, 1,500 KES → Loans, 1,200 KES → Contributions<br/>
                        <strong>Why:</strong> Priority order: Fines → Loans → Contributions
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end mt-6 space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reset all payment routing settings to defaults? This will remove all your custom settings including member targets, contribution amounts, and reminder settings.')) {
                        resetPaymentRoutingSettings.mutate(undefined, {
                          onSuccess: () => {
                            toast.success('Payment routing settings have been reset to defaults');
                          },
                          onError: (error) => {
                            toast.error(getApiErrorMessage(error) || 'Failed to reset settings');
                          }
                        });
                      }
                    }}
                    disabled={resetPaymentRoutingSettings.isPending}
                    className="flex items-center px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600 disabled:opacity-50"
                  >
                    {resetPaymentRoutingSettings.isPending ? 'Resetting...' : 'Reset to Defaults'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const settingsToSave = localPaymentRoutingSettings || paymentRoutingSettings;
                      updatePaymentRoutingSettings.mutate(settingsToSave, {
                        onSuccess: () => {
                          setHasInteractedPaymentRouting(false);
                        },
                      });
                    }}
                    disabled={updatePaymentRoutingSettings.isPending || !localPaymentRoutingSettings || !paymentRoutingDirty}
                    className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="mr-2 w-4 h-4" />
                    {updatePaymentRoutingSettings.isPending ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
                  </div>
                )}
              </div>
            )}

            {/* SMS Templates Tab */}
            {activeTab === 'sms-templates' && isAdmin && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h2 className="mb-6 text-2xl font-bold text-gray-800">SMS Templates</h2>
                  <p className="mb-6 text-sm text-gray-600">
                    Customize SMS messages sent to members. Use placeholders to personalize messages. 
                    If no custom template is set, the system default will be used.
                  </p>

                  <SmsTemplatesContent localPaymentRoutingSettings={localPaymentRoutingSettings} profile={profile} />
                </div>
              </div>
            )}

            {/* Loans Tab */}
            {activeTab === 'loans' && isAdmin && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h2 className="mb-6 text-2xl font-bold text-gray-800">Loan Settings</h2>
                  
                  {/* Sub-tabs for Loans */}
                  <div className="flex gap-4 mb-6 border-b border-gray-200">
                    <button
                      onClick={() => setLoansSubTab('loan-settings')}
                      className={`pb-3 px-4 font-medium transition-colors ${
                        loansSubTab === 'loan-settings'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Loan Eligibility Settings
                    </button>
                    <button
                      onClick={() => setLoansSubTab('table-banking')}
                      className={`pb-3 px-4 font-medium transition-colors ${
                        loansSubTab === 'table-banking'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Table Banking Settings
                    </button>
                  </div>

                  {/* Loan Settings Content */}
                  {loansSubTab === 'loan-settings' && (
                    <LoanSettingsContent />
                  )}

                  {/* Table Banking Settings Content */}
                  {loansSubTab === 'table-banking' && (
                    <TableBankingSettingsContent />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="container px-6 pb-8 mx-auto">
        <div className="mt-8 text-sm text-center text-gray-500">
          If you need more information, please reach out to the support line on the home page.
        </div>
      </div>

      {/* SMS Recharge Modal */}
      <SmsRechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        onSuccess={() => {
          setShowRechargeModal(false);
          // The modal will handle its own success state
        }}
      />


    </div>
  );
}